import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, Suspense } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Text, Edges } from '@react-three/drei';
import * as THREE from 'three';
import { useNavigate } from 'react-router-dom';
import s from './Structure.module.css';
import montserratBold from '../fonts/Montserrat-Bold.ttf';
import { ReactComponent as IconPlanet } from '../icons/planet_info_icon_full_ring.svg';
import planetInfoIconUrl from '../icons/planet_info_icon_full_ring.svg';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';

function sortByCode(a, b) {
  const A = (a?.code || '').toString().toLowerCase();
  const B = (b?.code || '').toString().toLowerCase();
  const an = Number(A), bn = Number(B);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
  return A.localeCompare(B, 'ru');
}

const PURPLE_HEX = '#7E0097';
const EDGE_HEX = '#D9A6E6'; // светлее для граней/углов

const STAR_CORE = '#FFE8A0';
const STAR_RING = '#FFD54A';
const STAR_GLOW = '#FFD76A';
const STAR_GLOW_SOFT = '#FFEFBE';

const MY_STAR_CORE = '#FFE9A8';
const MY_STAR_RING = '#FFC933';
const MY_STAR_GLOW = '#FFBE3B';
const MY_STAR_GLOW_SOFT = '#FFE7A3';
const PURPLE = new THREE.Color(PURPLE_HEX);

// Базовый origin бэкенда: отрезаем /api/v* у API_URL (как в Ratings.js)
const API_ORIGIN = (() => {
  try {
    return API_URL.replace(/\/api\/v\d+\/?$/i, '');
  } catch {
    return '';
  }
})();

// Правильное построение src для аватаров (как в Ratings.js)
function resolveAvatar(url) {
  if (!url) return null;
  if (url.startsWith('/uploads/')) return `${API_ORIGIN}${url}`;
  return url;
}

// высота (толщина) объёмного кольца
const BAND_HEIGHT = 0;

// Камера “сверху” смотрит примерно по -Y
const DOWN = new THREE.Vector3(0, -1, 0);

function makeExtrudedRingGeometry(innerR, outerR, height, segments = 48) {
  const shape = new THREE.Shape();
  shape.absarc(0, 0, outerR, 0, Math.PI * 2, false);

  if (innerR > 0.000001) {
    const hole = new THREE.Path();
    hole.absarc(0, 0, innerR, 0, Math.PI * 2, true);
    shape.holes.push(hole);
  }

  const geom = new THREE.ExtrudeGeometry(shape, {
    depth: height,
    bevelEnabled: false,
    curveSegments: segments,
  });

  geom.translate(0, 0, -height / 2);
  geom.computeVertexNormals();
  return geom;
}

function pickLeagueCode(me) {
  const raw = me?.league?.code ?? null;
  const s = raw == null ? null : String(raw).trim();
  return s ? s.toUpperCase() : null;
}

function OrbitGlowRing({ radius, y, color = '#BD23DB', width = 0.02, opacity = 0.22 }) {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, y + 0.012, 0]}
      renderOrder={2600}
    >
      <ringGeometry args={[radius - width / 2, radius + width / 2, 64]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  );
}

function RankBand3D({ innerR, outerR, y, opacity, color, onClick, isHighlighted }) {
  const geom = useMemo(
    () => makeExtrudedRingGeometry(innerR, outerR, BAND_HEIGHT, 96),
    [innerR, outerR]
  );

  const matOpacity = isHighlighted ? 0.55 : 0.30;
  const emissiveIntensity = isHighlighted ? 0.7 : 0.35;

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, y, 0]}
      geometry={geom}
      castShadow
      receiveShadow
      onClick={onClick}
    >
      <meshStandardMaterial
        color={color}
        transparent
        opacity={matOpacity}
        roughness={0.72}
        metalness={0.04}
        emissive={new THREE.Color(color).multiplyScalar(isHighlighted ? 0.25 : 0.08)}
        emissiveIntensity={emissiveIntensity}
        depthWrite={true}
        depthTest={true}
      />
    </mesh>
  );
}

function CurvedLetter({
  char,
  radius,
  localAngle,
  fontSize,
  color,
  opacity = 1,
  onClick,
}) {
  const ref = useRef(null);

  useFrame(() => {
    const g = ref.current;
    if (!g || !g.parent) return;

    const centerAngle = g.parent.userData.centerAngle ?? 0;
    const angle = centerAngle + localAngle;

    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;

    g.position.set(x, 0, z);

    // поворот по касательной к окружности
    g.rotation.set(0, -angle + Math.PI / 2, 0);
  });

  return (
    <group ref={ref}>
      <Text
        font={montserratBold}
        color={color}
        fontSize={fontSize}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.002}
        outlineColor="#000000"
        fillOpacity={opacity}
        outlineOpacity={opacity}
        onClick={(e) => {
          e.stopPropagation?.();
          onClick?.();
        }}
      >
        {char}
      </Text>
    </group>
  );
}

//  Название отдела на боковой стенке внешнего кольца (по центру стенки),
// всегда на “передней” стороне относительно камеры
function DepartmentSideName3D({
  name,
  y,
  outerR,
  hidden,
  fontSize = 0.11,
  color = '#ffed8a',
  dimmed = false,
  showInfoIcon = false,
  onInfoClick,
}) {
  const groupRef = useRef(null);


  useFrame(({ camera }) => {
    const g = groupRef.current;
    if (!g) return;

    g.visible = !hidden;

    // берем направление камеры в XZ и ищем "переднюю" точку круга
    const dir = new THREE.Vector3(camera.position.x, 0, camera.position.z);
    if (dir.lengthSq() < 1e-10) dir.set(0, 0, 1);
    dir.normalize();

    // угол центра надписи — там, где круг "смотрит" на камеру
    const centerAngle = Math.atan2(dir.z, dir.x);

    // ставим группу в центр диска по Y, дальше буквы сами разложатся вокруг
    g.position.set(0, y + 0.06, 0);
    g.userData.centerAngle = centerAngle;
  });

  const text = String(name ?? '').toUpperCase();
  const chars = [...text];

  // радиус, по которому пойдет текст
  const textRadius = outerR + 0.18;

  // примерный шаг между буквами в радианах
  // можно подкрутить 0.075 / 0.085 / 0.095
  const angleStep = 0.07;

  return (
    <group ref={groupRef} renderOrder={3000}>
      {chars.map((char, i) => {
        const startAngle =
          (chars.length - 1) * angleStep * 0.5;
        const localAngle = startAngle - i * angleStep;

        return (
          <CurvedLetter
            key={`${char}_${i}`}
            char={char}
            radius={textRadius}
            localAngle={localAngle}
            fontSize={fontSize}
            color={color}
            opacity={dimmed ? 0.3 : 1}
          />
        );
      })}
      {showInfoIcon && onInfoClick && (
        <PlanetInfoIcon3D
          radius={textRadius + 0.03}
          // Иконка сразу после последней буквы
          localAngle={
            (chars.length - 1) * angleStep * 0.5 - chars.length * angleStep - 0.03
          }
          fontSize={fontSize}
          opacity={dimmed ? 0.3 : 1}
          onClick={onInfoClick}
        />
      )}
    </group>
  );
}

function PlanetInfoIcon3D({ radius, localAngle, fontSize = 0.11, opacity = 1, onClick }) {
  const spriteRef = useRef(null);
  const texture = useLoader(THREE.TextureLoader, planetInfoIconUrl);
  const [aspect, setAspect] = useState(1);

  useEffect(() => {
    const w = texture?.image?.width;
    const h = texture?.image?.height;
    if (typeof w === 'number' && typeof h === 'number' && h !== 0) {
      setAspect(w / h);
    }
  }, [texture]);

  useFrame(() => {
    const s = spriteRef.current;
    if (!s || !s.parent) return;
    const centerAngle = s.parent.userData.centerAngle ?? 0;
    const angle = centerAngle + localAngle;

    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    s.position.set(x, 0, z);
  });

  const baseSize = fontSize * 1.05;
  const w = baseSize * aspect;
  const h = baseSize;

  return (
    <sprite
      ref={spriteRef}
      position={[0, 0, 0]}
      scale={[w, h, 1]}
      onPointerDown={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <spriteMaterial attach="material" map={texture} transparent opacity={opacity} depthWrite={false} depthTest={false} />
    </sprite>
  );
}

function AutoFitCamera({ radius, yMax = 0, padding = 1.45 }) {
  const { camera, size } = useThree();
  const dirRef = useRef(new THREE.Vector3());
  const initialFitDoneRef = useRef(false);
  useLayoutEffect(() => {
    if (!radius || radius <= 0) return;
    if (yMax <= 0) return;
    if (initialFitDoneRef.current) return;
    initialFitDoneRef.current = true;

    const aspect = size.width / Math.max(1, size.height);
    const fov = THREE.MathUtils.degToRad(camera.fov);

    const safeAspect = Math.max(aspect, 0.35);
    const tan = Math.tan(fov / 2);
    const halfH = Math.max(0, yMax) * 0.5;

    const effective = Math.sqrt(radius * radius + halfH * halfH);

    const distW = effective / (tan * safeAspect);
    const distH = effective / tan;

    const dist = Math.max(distW, distH) * padding;

    const camY = yMax + dist;
    const targetY = halfH;

    camera.position.set(0, camY, 0.001);
    camera.lookAt(0, targetY, 0);

    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, radius, yMax, padding]);

  useFrame(() => {
    const ENTER_TOP = 0.18;

    camera.getWorldDirection(dirRef.current);
    const cos = THREE.MathUtils.clamp(dirRef.current.dot(DOWN), -1, 1);
    const theta = Math.acos(cos);

    const isTop = theta < ENTER_TOP;
    if (!isTop) return;

    const minY = yMax + 0.35;
    if (camera.position.y < minY) {
      camera.position.y = minY;
    }
  });

  return null;
}

// Управление камерой на мобильных: фиксированные виды "сверху" / "сбоку"
function MobileCameraController({ isTopView, radius, yMax, focusY = null, tiltDeg = 50, controlsRef }) {
  const { camera, size } = useThree();
  const dirRef = useRef(new THREE.Vector3());
  const desiredPosRef = useRef(new THREE.Vector3());
  const desiredTargetRef = useRef(new THREE.Vector3(0, 0, 0));
  const lastFocusYRef = useRef(null);

  useLayoutEffect(() => {
    if (!radius || radius <= 0) return;

    const aspect = size.width / Math.max(1, size.height);
    const fov = THREE.MathUtils.degToRad(camera.fov);
    const tan = Math.tan(fov / 2);

    const safeAspect = Math.max(aspect, 0.35);
    const halfH = Math.max(0, yMax) * 0.5;
    const effective = Math.sqrt(radius * radius + halfH * halfH);

    const distW = effective / (tan * safeAspect);
    const distH = effective / tan;
    const baseDist = Math.max(distW, distH);

    if (isTopView) {
      const dist = baseDist * 1.35;
      const camY = yMax + dist;
      const targetY = halfH;
      desiredPosRef.current.set(0, camY, 0.001);
      desiredTargetRef.current.set(0, targetY, 0);
    } else {
      const dist = baseDist * 1.25;

      // Без выбранного отдела оставляем старое боковое поведение (камера смотрит в центр структуры).
      if (focusY == null) {
        const tiltAngle = THREE.MathUtils.degToRad(20); // угол наклона камеры сверху
        const horiz = dist * Math.cos(tiltAngle);
        const vert = dist * Math.sin(tiltAngle);
        const xz = horiz / Math.sqrt(2);
        const lookAtY = halfH * 1.4;
        desiredPosRef.current.set(xz, lookAtY + vert, xz);
        desiredTargetRef.current.set(0, lookAtY, 0);
      } else {
        // При выборе отдела наклоняем камеру примерно на tiltDeg градусов к уровню этого отдела.
        const targetY = Number(focusY);
        const tilt = THREE.MathUtils.degToRad(
          THREE.MathUtils.clamp(Number(tiltDeg) || 50, 10, 80)
        );
        const horiz = dist * Math.cos(tilt);
        const yOff = dist * Math.sin(tilt);
        const xz = horiz / Math.sqrt(2);
        desiredPosRef.current.set(xz, targetY + yOff, xz);
        desiredTargetRef.current.set(0, targetY, 0);

        // Запускаем анимацию только если меняется фокус по Y (выбран другой диск).
        if (lastFocusYRef.current !== targetY) {
          lastFocusYRef.current = targetY;
        }
      }
    }

    // Мгновенно выставляем при первом расчёте (resize / смена режима),
    // дальше (если есть выбор отдела) дотягиваем плавно в useFrame.
    camera.position.copy(desiredPosRef.current);
    camera.lookAt(desiredTargetRef.current);
    const c = controlsRef?.current;
    if (c) {
      c.target.copy(desiredTargetRef.current);
      c.update();
    }
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, isTopView, radius, yMax, focusY, tiltDeg, controlsRef]);

  // Не даём камере на мобилке "провалиться" ниже верхнего отдела в виде сверху
  useFrame(() => {
    if (!isTopView) return;

    const ENTER_TOP = 0.18;

    camera.getWorldDirection(dirRef.current);
    const cos = THREE.MathUtils.clamp(dirRef.current.dot(DOWN), -1, 1);
    const theta = Math.acos(cos);

    const isTop = theta < ENTER_TOP;
    if (!isTop) return;

    const minY = yMax + 0.35;
    if (camera.position.y < minY) {
      camera.position.y = minY;
    }
  });

  return null;
}

function MobileOrbitSoftener({ controlsRef, isMobile, isTopView }) {
  useFrame(({ camera }) => {
    if (!isTopView) return;
    const controls = controlsRef.current;
    if (!controls) return;

    const camY = camera.position.y;

    const CLOSE = 4.5;
    const FAR = 20.0;

    if (isMobile) {
      let t = THREE.MathUtils.inverseLerp(FAR, CLOSE, camY);
      t = THREE.MathUtils.clamp(t, 0, 1);
      t = t * t;

      controls.panSpeed = THREE.MathUtils.lerp(0.45, 0.05, t);
      controls.zoomSpeed = THREE.MathUtils.lerp(0.4, 0.08, t);
      controls.dampingFactor = THREE.MathUtils.lerp(0.16, 0.60, t);
    } else {
      let t = THREE.MathUtils.inverseLerp(FAR, CLOSE, camY);
      t = THREE.MathUtils.clamp(t, 0, 1);
      t = t * t;

      if (isTopView) {
        controls.panSpeed = THREE.MathUtils.lerp(0.55, 0.02, t);
        controls.zoomSpeed = THREE.MathUtils.lerp(1.0, 0.15, t);
        controls.dampingFactor = THREE.MathUtils.lerp(0.12, 0.35, t);
      }
    }
  });

  return null;
}

//  кламп радиуса внутри полосы, чтобы маркер не вылезал наружу
function clampRadiusInBand(inner, outer, desired, markerRadius = 0) {
  const thickness = outer - inner;
  if (thickness <= 0) return desired;

  const safe = Math.min(thickness * 0.12, markerRadius * 0.7);

  const minR = inner + safe;
  const maxR = outer - safe;

  if (maxR <= minR) return (inner + outer) / 2;
  return THREE.MathUtils.clamp(desired, minR, maxR);
}

function TopViewTracker({ onModeChange, onTransitionChange }) {
  const lastMode = useRef(true);
  const lastTransition = useRef(false);
  const dirRef = useRef(new THREE.Vector3());

  useFrame(({ camera }) => {
    camera.getWorldDirection(dirRef.current);
    const cos = THREE.MathUtils.clamp(dirRef.current.dot(DOWN), -1, 1);
    const theta = Math.acos(cos);

    const ENTER_TOP = 0.18;
    const EXIT_TOP = 0.30;

    const inTransition = theta >= ENTER_TOP && theta <= EXIT_TOP;

    let nextMode = lastMode.current;

    if (theta < ENTER_TOP) nextMode = true;
    if (theta > EXIT_TOP) nextMode = false;

    if (inTransition !== lastTransition.current) {
      lastTransition.current = inTransition;
      onTransitionChange?.(inTransition);
    }

    if (nextMode !== lastMode.current) {
      lastMode.current = nextMode;
      onModeChange?.(nextMode);
    }
  });

  return null;
}

const LIFT_FOR_LINES = 0.001;

// Позиция точки на диске по данным dot (r, angle, y) — как у аватаров
function dotToPosition(dot) {
  const x = dot.r * Math.cos(dot.angle);
  const z = dot.r * Math.sin(dot.angle);
  return [x, (dot.y ?? 0) + LIFT_FOR_LINES, z];
}

// Кривая «хвост кометы» между двумя точками: дуга вверх + мягкое свечение + летящая "частица"
function ConnectionLine3D({ start, end, phaseOffset = 0 }) {
  const glowMatRef = useRef(null);
  const cometRef = useRef(null);
  const { curve, length } = useMemo(() => {
    const s = new THREE.Vector3(...start);
    const e = new THREE.Vector3(...end);
    const len = s.distanceTo(e);
    if (len < 1e-6) return { curve: null, length: 0 };
    const mid = new THREE.Vector3().addVectors(s, e).multiplyScalar(0.5);
    // Контрольная точка дуги выше середины — как у падающей звезды
    const arcHeight = len * 0.35;
    const control = new THREE.Vector3(mid.x, mid.y + arcHeight, mid.z);
    const curve = new THREE.QuadraticBezierCurve3(s, control, e);
    return { curve, length: len };
  }, [start, end]);

  useFrame(({ clock }) => {
    if (!curve || length < 1e-6) return;

    const t = clock.getElapsedTime() + phaseOffset;

    if (glowMatRef.current) {
      glowMatRef.current.opacity = 0.25 + 0.18 * Math.sin(t * 1.2);
    }

    // "частица-комета", бегущая по дуге
    if (cometRef.current) {
      const speed = 0.28; // базовая скорость движения по дуге
      const travel = (t * speed) % 1;
      const pulse = 0.8 + 0.35 * Math.sin(t * 2.0); // лёгкая пульсация размера

      const p = curve.getPoint(travel);
      cometRef.current.position.set(p.x, p.y, p.z);
      cometRef.current.scale.setScalar(pulse);
    }
  });

  if (!curve || length < 1e-6) return null;

  const lineRadius = 0.0015;
  const glowRadius = 0.005;
  const tubularSegments = Math.max(24, Math.min(64, Math.floor(length * 80)));
  const radialSegments = 6;

  return (
    <group>
      {/* Мягкое свечение по дуге */}
      <mesh renderOrder={100}>
        <tubeGeometry args={[curve, tubularSegments, glowRadius, radialSegments]} />
        <meshBasicMaterial
          ref={glowMatRef}
          color="#FFE8A0"
          transparent
          opacity={0.35}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>
      {/* Тонкое ядро — дуга */}
      <mesh renderOrder={101}>
        <tubeGeometry args={[curve, tubularSegments, lineRadius, radialSegments]} />
        <meshBasicMaterial
          color="#FFD54A"
          transparent
          opacity={0.65}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>
      {/* "комета" — светящаяся частица, бегущая вдоль дуги */}
      <mesh ref={cometRef} renderOrder={102}>
        <sphereGeometry args={[glowRadius * 0.55, 12, 12]} />
        <meshBasicMaterial
          color="#FFF6C0"
          transparent
          opacity={0.9}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>
    </group>
  );
}

const textureCache = new Map();   // url → THREE.Texture
const loadingSet = new Set();     // url — сейчас грузится
const MAX_TEXTURES = 40;          // максимум в GPU

function loadTexture(url, onLoad) {
  if (textureCache.has(url)) {
    onLoad(textureCache.get(url));
    return;
  }
  if (loadingSet.has(url)) return;
  if (textureCache.size >= MAX_TEXTURES) return; // лимит достигнут

  loadingSet.add(url);
  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin('anonymous');
  loader.load(url, (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace;
    textureCache.set(url, tex);
    loadingSet.delete(url);
    onLoad(tex);
  });
}

function evictFarTextures(dots, cameraPos, keepCount = 30) {
  // Сортируем по дистанции, удаляем дальние текстуры из кэша
  const withDist = [...textureCache.entries()].map(([url, tex]) => {
    const dot = dots.find(d => resolveAvatar(d.avatarUrl) === url);
    if (!dot) return { url, dist: Infinity };
    const x = dot.r * Math.cos(dot.angle);
    const z = dot.r * Math.sin(dot.angle);
    return { url, dist: cameraPos.distanceTo(new THREE.Vector3(x, dot.y, z)) };
  });

  withDist.sort((a, b) => a.dist - b.dist);

  // Оставляем только ближайшие keepCount
  withDist.slice(keepCount).forEach(({ url }) => {
    const tex = textureCache.get(url);
    if (tex) tex.dispose();
    textureCache.delete(url);
  });
}

/**
 * аватар как 3D-объект (не Html).
 * Делается круг (CircleGeometry) с текстурой + белая окантовка (RingGeometry).
 * Группа "смотрит" на камеру, чтобы аватар всегда был лицом к пользователю.
 */
function UserAvatarMarker3D({
  r,
  angle,
  y,
  texture,
  radius = 0.06,
  onClick,
  onLongPress,
  onPointerDownSignal,
  isMe = false,
  isHighlighted = false,
  isOrbit = false,
  onPositionUpdate,
  orbitLastMoveAtRef,
}) {
  const x = r * Math.cos(angle);
  const z = r * Math.sin(angle);

  const groupRef = useRef(null);
  const pointerDownRef = useRef({ x: null, y: null, moved: false, orbitMoveAtDown: 0 });
  const MOVE_TOLERANCE_PX = 12;
  const glowOuterRef = useRef(null);
  const glowMidRef = useRef(null);
  const glowInnerRef = useRef(null);

  const outerGlowMatRef = useRef(null);
  const midGlowMatRef = useRef(null);
  const innerGlowMatRef = useRef(null);
  const ringBackMatRef = useRef(null);
  const ringMidMatRef = useRef(null);
  const ringFrontMatRef = useRef(null);
  const avatarMatRef = useRef(null);
  const isPressedRef = useRef(false);
  const pressTimer = useRef(null);
  const didLongPress = useRef(false);
  const didDoubleClick = useRef(false);
  const lastClickTime = useRef(0);
  const canPreviewRef = useRef(false);
  const pressStartedAtRef = useRef(0);

  const coreColor = isMe ? MY_STAR_CORE : STAR_CORE;
  const ringColor = isMe ? MY_STAR_RING : STAR_RING;
  const glowColor = isMe ? MY_STAR_GLOW : STAR_GLOW;
  const glowSoftColor = isMe ? MY_STAR_GLOW_SOFT : STAR_GLOW_SOFT;

  useEffect(() => {
    if (!texture) return;
    if ('colorSpace' in texture) texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;

    if (avatarMatRef.current) {
      avatarMatRef.current.map = texture;
      avatarMatRef.current.needsUpdate = true;
    }
  }, [texture]);

  const TOP_THETA = 0.22;
  const LIFT_TOP = 0.001;
  const LIFT_3D = BAND_HEIGHT / 2 + 0.001;

  const dirRef = useRef(new THREE.Vector3());
  const posRef = useRef(new THREE.Vector3());

  useFrame(({ camera, clock }) => {
    const g = groupRef.current;
    if (!g) return;

    let ex = r * Math.cos(angle);
    let ez = r * Math.sin(angle);

    camera.getWorldDirection(dirRef.current);
    const cos = THREE.MathUtils.clamp(dirRef.current.dot(DOWN), -1, 1);
    const theta = Math.acos(cos);
    const isTop = theta < TOP_THETA;

    const lift = isTop ? LIFT_TOP : LIFT_3D;

    if (isOrbit) {
      if (!isTop) {
        const camDir = new THREE.Vector3(camera.position.x, 0, camera.position.z);
        if (camDir.lengthSq() > 1e-10) {
          camDir.normalize();
          const frontAngle = Math.atan2(camDir.z, camDir.x);
          let delta = angle - frontAngle;
          while (delta > Math.PI) delta -= Math.PI * 2;
          while (delta < -Math.PI) delta += Math.PI * 2;

          const TEXT_HALF_SPAN = 0.65;
          const MARGIN = 0.15;
          if (Math.abs(delta) < TEXT_HALF_SPAN) {
            const shifted = delta >= 0
              ? frontAngle + TEXT_HALF_SPAN + MARGIN
              : frontAngle - TEXT_HALF_SPAN - MARGIN;
            ex = r * Math.cos(shifted);
            ez = r * Math.sin(shifted);
          }
        }
      }
    }

    g.position.set(ex, y + lift, ez);

    if (!isTop) {
      g.position.y += 0.04;
    }

    if (isTop) {
      g.lookAt(camera.position);
      g.rotation.z = 0;
    } else {
      const dx = camera.position.x - g.position.x;
      const dz = camera.position.z - g.position.z;
      const yaw = Math.atan2(dz, dx);
      g.rotation.set(0, -yaw + Math.PI / 2, 0);
    }

    posRef.current.copy(g.position);
    const dist = camera.position.distanceTo(posRef.current);
    onPositionUpdate?.([g.position.x, g.position.y, g.position.z]);

    if (isTop) {
      const FAR_DIST = 3.6;
      const NEAR_DIST = 1.5;

      let t = THREE.MathUtils.inverseLerp(FAR_DIST, NEAR_DIST, dist);
      t = THREE.MathUtils.clamp(t, 0, 1);
      t = t * t * (3 - 2 * t);

      const minScale = 0.18;
      const maxScale = 0.32 * 2;
      const scale = THREE.MathUtils.lerp(minScale, maxScale, t);
      g.scale.setScalar(scale);

      const farGlowAlpha = THREE.MathUtils.lerp(
        isMe ? 0.12 : 0.06,
        isMe ? 0.04 : 0.02,
        t
      );

      const midGlowAlpha = THREE.MathUtils.lerp(
        isMe ? 0.16 : 0.10,
        isMe ? 0.08 : 0.04,
        t
      );

      const innerGlowAlpha = THREE.MathUtils.lerp(
        isMe ? 0.22 : 0.14,
        isMe ? 0.12 : 0.06,
        t
      );

      if (outerGlowMatRef.current) outerGlowMatRef.current.opacity = farGlowAlpha;
      if (midGlowMatRef.current) midGlowMatRef.current.opacity = midGlowAlpha;
      if (innerGlowMatRef.current) innerGlowMatRef.current.opacity = innerGlowAlpha;

      if (ringBackMatRef.current) ringBackMatRef.current.opacity = isMe ? 0.18 : 0.12;
      if (ringMidMatRef.current) ringMidMatRef.current.opacity = isMe ? 0.50 : 0.35;
      if (ringFrontMatRef.current) ringFrontMatRef.current.opacity = 1.0;

      let avatarReveal = THREE.MathUtils.mapLinear(t, 0.82, 1.0, 0, 1);
      avatarReveal = THREE.MathUtils.clamp(avatarReveal, 0, 1);
      avatarReveal = avatarReveal * avatarReveal * (3 - 2 * avatarReveal);

      if (avatarMatRef.current) {
        avatarMatRef.current.opacity = avatarReveal;
      }
    } else {
      // ---------- SIDE / NORMAL VIEW ----------
      g.scale.setScalar(0.5);

      if (outerGlowMatRef.current) outerGlowMatRef.current.opacity = isMe ? 0.05 : 0.02;
      if (midGlowMatRef.current) midGlowMatRef.current.opacity = isMe ? 0.08 : 0.03;
      if (innerGlowMatRef.current) innerGlowMatRef.current.opacity = isMe ? 0.12 : 0.05;

      if (ringBackMatRef.current) ringBackMatRef.current.opacity = isMe ? 0.08 : 0.10;
      if (ringMidMatRef.current) ringMidMatRef.current.opacity = isMe ? 0.38 : 0.20;
      if (ringFrontMatRef.current) ringFrontMatRef.current.opacity = isMe ? 0.7 : 0.55;

      if (avatarMatRef.current) {
        avatarMatRef.current.opacity = 1;
      }
    }

    if (isMe) {
      const pulse = 1 + Math.sin(clock.getElapsedTime() * 2.2) * 0.08;
      const pulse2 = 1 + Math.sin(clock.getElapsedTime() * 1.7 + 0.9) * 0.12;
      const pulse3 = 1 + Math.sin(clock.getElapsedTime() * 2.8 + 1.4) * 0.06;

      if (glowOuterRef.current) glowOuterRef.current.scale.setScalar(pulse);
      if (glowMidRef.current) glowMidRef.current.scale.setScalar(pulse2);
      if (glowInnerRef.current) glowInnerRef.current.scale.setScalar(pulse3);
      if (outerGlowMatRef.current) outerGlowMatRef.current.opacity = 0.08;
      if (midGlowMatRef.current) midGlowMatRef.current.opacity = 0.14;
      if (innerGlowMatRef.current) innerGlowMatRef.current.opacity = 0.20;
    } else {
      if (glowOuterRef.current) glowOuterRef.current.scale.setScalar(1);
      if (glowMidRef.current) glowMidRef.current.scale.setScalar(1);
      if (glowInnerRef.current) glowInnerRef.current.scale.setScalar(1);
    }

    if (isHighlighted) {
      const pulse = 1 + Math.sin(clock.getElapsedTime() * 3.0) * 0.15;
      if (glowOuterRef.current) {
        glowOuterRef.current.scale.setScalar(pulse * 1.3);
        outerGlowMatRef.current.opacity = 0.15;
      }
      if (glowMidRef.current) {
        glowMidRef.current.scale.setScalar(pulse * 1.15);
        midGlowMatRef.current.opacity = 0.25;
      }
      if (glowInnerRef.current) {
        glowInnerRef.current.scale.setScalar(pulse);
        innerGlowMatRef.current.opacity = 0.35;
      }
      if (ringFrontMatRef.current) {
        ringFrontMatRef.current.color.set(isHighlighted ? '#00E5FF' : coreColor);
      }
    }

    if (isPressedRef.current && canPreviewRef.current && !isHighlighted) {
      if (outerGlowMatRef.current) {
        outerGlowMatRef.current.opacity = Math.max(
          outerGlowMatRef.current.opacity,
          isMe ? 0.12 : 0.08,
        );
      }
      if (ringFrontMatRef.current) {
        ringFrontMatRef.current.color.set('#FFFFFF');
      }
    }
  });

  useEffect(() => {
    canPreviewRef.current = typeof onClick === 'function';
  }, [onClick]);

  return (
    <group
      ref={groupRef}
      position={[x, y + (BAND_HEIGHT / 3 + 0.03), z]}
      renderOrder={1000}
      onPointerDown={(ev) => {
        ev.stopPropagation();
        if (ev?.pointerType === 'touch') ev.preventDefault?.();

        isPressedRef.current = !!canPreviewRef.current;
        pressStartedAtRef.current = Date.now();

        pointerDownRef.current = {
          x: typeof ev?.clientX === 'number' ? ev.clientX : null,
          y: typeof ev?.clientY === 'number' ? ev.clientY : null,
          moved: false,
          orbitMoveAtDown: orbitLastMoveAtRef?.current ?? 0,
        };

        didLongPress.current = false;
        didDoubleClick.current = false;
        onPointerDownSignal?.(ev);

        const now = Date.now();
        if (lastClickTime.current !== 0 && now - lastClickTime.current < 400) {
          lastClickTime.current = 0;
          didDoubleClick.current = true;
          clearTimeout(pressTimer.current);
          isPressedRef.current = false;
          onLongPress?.();
          return;
        }

        pressTimer.current = setTimeout(() => {
          if (pointerDownRef.current?.moved) return;
          didLongPress.current = true;
          lastClickTime.current = 0;
          onClick?.();
        }, 400);
      }}
      onPointerMove={(ev) => {
        const start = pointerDownRef.current;
        if (!start || start.moved) return;
        if (start.x == null || start.y == null) return;

        const cx = typeof ev?.clientX === 'number' ? ev.clientX : null;
        const cy = typeof ev?.clientY === 'number' ? ev.clientY : null;
        if (cx == null || cy == null) return;

        const dist = Math.hypot(cx - start.x, cy - start.y);
        if (dist > MOVE_TOLERANCE_PX) {
          pointerDownRef.current.moved = true;
          clearTimeout(pressTimer.current);
          didLongPress.current = false;
          isPressedRef.current = false;
        }
      }}
      onPointerUp={(ev) => {
        ev.stopPropagation();
        if (ev?.pointerType === 'touch') ev.preventDefault?.();

        if (pointerDownRef.current?.moved) {
          pointerDownRef.current.moved = false;
          clearTimeout(pressTimer.current);
          didLongPress.current = false;
          didDoubleClick.current = false;
          isPressedRef.current = false;
          return;
        }

        clearTimeout(pressTimer.current);
        if (didLongPress.current) {
          didLongPress.current = false;
          isPressedRef.current = false;
          return;
        }
        if (didDoubleClick.current) {
          didDoubleClick.current = false;
          isPressedRef.current = false;
          return;
        }
        const now = Date.now();
        const dtSinceDown = now - (pressStartedAtRef.current || 0);
        if (canPreviewRef.current && dtSinceDown >= 180 && dtSinceDown <= 600) {
          onClick?.();
        } else if (now - lastClickTime.current < 400) {
          lastClickTime.current = 0;
          onLongPress?.();
        } else {
          lastClickTime.current = now;
        }
        isPressedRef.current = false;
      }}
      onPointerLeave={() => {
        clearTimeout(pressTimer.current);
        didLongPress.current = false;
        didDoubleClick.current = false;
        isPressedRef.current = false;
        pointerDownRef.current.moved = false;
      }}
      onPointerCancel={() => {
        clearTimeout(pressTimer.current);
        didLongPress.current = false;
        didDoubleClick.current = false;
        isPressedRef.current = false;
        pointerDownRef.current.moved = false;
      }}
    >
      <mesh renderOrder={1002} position={[0, 0, 0.004]}>
        <circleGeometry args={[radius * 2.6, 32]} />
        <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
      </mesh>

      {isMe && (
        <pointLight
          position={[0, 0, 0.08]}
          intensity={0.05}
          distance={0.8}
          color={glowColor}
        />
      )}

      <mesh ref={glowOuterRef} renderOrder={995}>
        <circleGeometry args={[radius * (isMe ? 1.6 : 2.1), 40]} />
        <meshBasicMaterial
          ref={outerGlowMatRef}
          color={glowSoftColor}
          transparent
          opacity={isMe ? 0.05 : 0.03}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      <mesh ref={glowMidRef} renderOrder={996}>
        <circleGeometry args={[radius * (isMe ? 1.35 : 1.65), 40]} />
        <meshBasicMaterial
          ref={midGlowMatRef}
          color={glowColor}
          transparent
          opacity={isMe ? 0.10 : 0.05}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      <mesh ref={glowInnerRef} renderOrder={997}>
        <circleGeometry args={[radius * (isMe ? 1.15 : 1.2), 40]} />
        <meshBasicMaterial
          ref={innerGlowMatRef}
          color={ringColor}
          transparent
          opacity={isMe ? 0.14 : 0.08}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      <mesh renderOrder={998}>
        {isMe ? (
          <>
            <circleGeometry args={[radius * 1.2, 40]} />
            <meshBasicMaterial
              ref={ringBackMatRef}
              color={glowSoftColor}
              transparent
              opacity={0.12}
              depthTest={false}
              depthWrite={false}
            />
          </>
        ) : (
          <>
            <circleGeometry args={[radius * 1.35, 40]} />
            <meshBasicMaterial
              ref={ringBackMatRef}
              color={glowSoftColor}
              transparent
              opacity={0.18}
              depthTest={false}
              depthWrite={false}
            />
          </>
        )}
      </mesh>

      <mesh renderOrder={999}>
        {isMe ? (
          <>
            <circleGeometry args={[radius * 1.32, 40]} />
            <meshBasicMaterial
              ref={ringMidMatRef}
              color={glowColor}
              transparent
              opacity={0.18}
              depthTest={false}
              depthWrite={false}
            />
          </>
        ) : (
          <>
            <circleGeometry args={[radius * 1.28, 40]} />
            <meshBasicMaterial
              ref={ringMidMatRef}
              color={glowColor}
              transparent
              opacity={0.18}
              depthTest={false}
              depthWrite={false}
            />
          </>
        )}
      </mesh>

      <mesh renderOrder={1000}>
        <circleGeometry args={[radius * 1.06, 40]} />
        <meshBasicMaterial
          ref={ringFrontMatRef}
          color={coreColor}
          transparent
          opacity={1}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      <mesh renderOrder={1001} position={[0, 0, 0.0008]}>
        <circleGeometry args={[radius, 40]} />
        <meshBasicMaterial
          ref={avatarMatRef}
          map={texture}
          transparent
          opacity={1}
          toneMapped={false}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

const AVATAR_LOAD_DIST = 1.8;

function TextureEvictManager({ allDots }) {
  const { camera } = useThree();
  const frameCount = useRef(0);

  useFrame(() => {
    frameCount.current++;
    if (frameCount.current % 120 !== 0) return;

    evictFarTextures(allDots, camera.position, 30);
  });

  return null;
}

function LazyAvatarMarker3D({
  r, angle, y, avatarUrl,
  radius,
  isMe,
  isHighlighted,
  onClick,
  onLongPress,
  onPointerDownSignal,
  isOrbit = false,
  onPositionUpdate,
  orbitLastMoveAtRef,
}) {
  const [texture, setTexture] = useState(null);
  const { camera } = useThree();
  const posRef = useRef(new THREE.Vector3());

  const x = r * Math.cos(angle);
  const z = r * Math.sin(angle);

  useFrame(() => {
    posRef.current.set(x, y, z);
    const dist = camera.position.distanceTo(posRef.current);

    if (dist < AVATAR_LOAD_DIST && !texture && avatarUrl) {
      const resolved = resolveAvatar(avatarUrl);
      loadTexture(resolved, setTexture);
    }
  });

  return (
    <UserAvatarMarker3D
      r={r} angle={angle} y={y}
      texture={texture}
      radius={radius}
      isMe={isMe}
      isHighlighted={isHighlighted}
      onClick={onClick}
      onLongPress={onLongPress}
      onPointerDownSignal={onPointerDownSignal}
      isOrbit={isOrbit}
      onPositionUpdate={onPositionUpdate}
      orbitLastMoveAtRef={orbitLastMoveAtRef}
    />
  );
}

function EagerAvatarMarker3D({
  r,
  angle,
  y,
  avatarUrl,
  radius,
  isMe,
  isHighlighted,
  onClick,
  onLongPress,
  onPointerDownSignal,
  isOrbit = false,
  onPositionUpdate,
  orbitLastMoveAtRef,
}) {
  const [texture, setTexture] = useState(null);

  useEffect(() => {
    if (!avatarUrl) return;
    const resolved = resolveAvatar(avatarUrl);
    loadTexture(resolved, setTexture);
  }, [avatarUrl]);

  return (
    <UserAvatarMarker3D
      r={r} angle={angle} y={y}
      texture={texture}
      radius={radius}
      isMe={isMe}
      isHighlighted={isHighlighted}
      onClick={onClick}
      onLongPress={onLongPress}
      onPointerDownSignal={onPointerDownSignal}
      isOrbit={isOrbit}
      onPositionUpdate={onPositionUpdate}
      orbitLastMoveAtRef={orbitLastMoveAtRef}
    />
  );
}

function CosmicLoader({ text }) {
  const STARS = [
    [18, 22], [80, 8], [155, 30], [170, 90], [155, 160],
    [90, 188], [25, 165], [5, 95], [140, 52], [42, 140],
    [110, 175], [175, 130], [60, 18], [130, 10]
  ]
  return (
    <div className={s.loading}>
      <div className={s.loaderWrap}>
        <div className={s.stars}>
          {STARS.map(([x, y], i) => (
            <div key={i} className={s.star} style={{ left: x, top: y, animationDelay: `${(i * 0.3) % 2}s`, animationDuration: `${1.4 + (i % 5) * 0.4}s` }} />
          ))}
        </div>
        <div className={`${s.ring} ${s.ring1}`}>
          <div className={`${s.dotOrbit} ${s.dotOrbit1}`}><div className={`${s.dot} ${s.dot1}`} /></div>
        </div>
        <div className={`${s.ring} ${s.ring2}`}>
          <div className={`${s.dotOrbit} ${s.dotOrbit2}`}><div className={`${s.dot} ${s.dot2}`} /></div>
        </div>
        <div className={`${s.ring} ${s.ring3}`}>
          <div className={`${s.dotOrbit} ${s.dotOrbit3}`}><div className={`${s.dot} ${s.dot3}`} /></div>
        </div>
        <div className={`${s.ring} ${s.ring4}`}>
          <div className={`${s.dotOrbit} ${s.dotOrbit4}`}><div className={`${s.dot} ${s.dot4}`} /></div>
        </div>
        <div className={s.core} />
      </div>
      <div className={s.loadingText}>{text}</div>
    </div>
  )
}

function createCircleSpriteTexture() {
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  const dim = 64;
  canvas.width = dim;
  canvas.height = dim;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const r = dim / 2;
  const cx = r;
  const cy = r;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.65, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');

  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  return tex;
}

function StarLayer({ count, radius, size, opacity, colorA, colorB, twinkleSpeed = 0.25, drift = 0.02, sprite = 'square' }) {
  const ref = useRef(null);
  const { positions, colors } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const A = new THREE.Color(colorA);
    const B = new THREE.Color(colorB);

    for (let i = 0; i < count; i++) {
      const r = radius * (0.35 + Math.random() * 0.75);
      const a = Math.random() * Math.PI * 2;
      const h = (Math.random() - 0.5) * radius * 1.6;

      pos[i * 3 + 0] = Math.cos(a) * r;
      pos[i * 3 + 1] = h;
      pos[i * 3 + 2] = Math.sin(a) * r;

      const t = Math.pow(Math.random(), 1.4);
      const c = A.clone().lerp(B, t);
      col[i * 3 + 0] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    return { positions: pos, colors: col };
  }, [count, radius, colorA, colorB]);

  const circleSprite = useMemo(() => {
    if (sprite !== 'circle') return null;
    return createCircleSpriteTexture();
  }, [sprite]);

  useFrame(({ clock }) => {
    const p = ref.current;
    if (!p) return;
    const t = clock.getElapsedTime();
    p.rotation.y = t * drift;
    if (p.material) {
      const tw = 0.85 + 0.15 * Math.sin(t * (1.2 + twinkleSpeed));
      p.material.opacity = opacity * tw;
    }
  });

  return (
    <points ref={ref} renderOrder={1}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        vertexColors
        transparent
        map={circleSprite ?? undefined}
        alphaTest={circleSprite ? 0.01 : undefined}
        opacity={opacity}
        depthWrite={false}
        sizeAttenuation
      />
    </points>
  );
}

function Starfield({ quality = 'high', radius = 18 }) {
  const q = quality === 'low' ? 0.45 : 1;
  const far = Math.floor(3200 * q);
  const mid = Math.floor(850 * q);
  const near = Math.floor(420 * q);

  return (
    <group>
      <StarLayer
        count={far}
        radius={radius * 1.35}
        size={0.09}
        opacity={0.38}
        colorA="#BFA7FF"
        colorB="#FFF3C8"
        twinkleSpeed={0.20}
        drift={0.006}
        sprite="circle"
      />
      <StarLayer
        count={mid}
        radius={radius * 1.05}
        size={0.07}
        opacity={0.52}
        colorA="#C9B7FF"
        colorB="#FFE9A8"
        twinkleSpeed={0.28}
        drift={0.012}
        sprite="circle"
      />
      <StarLayer
        count={near}
        radius={radius * 0.85}
        size={0.07}
        opacity={0.62}
        colorA="#E9C6FE"
        colorB="#FFF6C0"
        twinkleSpeed={0.38}
        drift={0.020}
        sprite="circle"
      />
    </group>
  );
}

function Meteors({ count = 6 }) {
  const meteors = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      delay: Math.random() * 12,
      length: 0.8 + Math.random() * 1.4,
      speed: 0.18 + Math.random() * 0.22,
      startX: (Math.random() - 0.5) * 28,
      startY: 8 + Math.random() * 10,
      startZ: (Math.random() - 0.5) * 28,
      dirX: (Math.random() - 0.5) * 0.4,
      dirY: -(0.6 + Math.random() * 0.4),
      dirZ: (Math.random() - 0.5) * 0.4,
    }));
  }, [count]);

  return (
    <group>
      {meteors.map((m) => (
        <Meteor key={m.id} {...m} />
      ))}
    </group>
  );
}

function Meteor({ delay, length, speed, startX, startY, startZ, dirX, dirY, dirZ }) {
  const headRef = useRef(null);
  const tailMatRef = useRef(null);
  const headMatRef = useRef(null);

  const dir = useMemo(() => {
    const v = new THREE.Vector3(dirX, dirY, dirZ);
    v.normalize();
    return v;
  }, [dirX, dirY, dirZ]);

  const { tailCurve } = useMemo(() => {
    const tail = new THREE.LineCurve3(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(-dir.x * length, -dir.y * length, -dir.z * length)
    );
    return { tailCurve: tail };
  }, [dir, length]);

  useFrame(({ clock }) => {
    const t = (clock.getElapsedTime() - delay) % 14;
    const active = t >= 0 && t < 3;

    const head = headRef.current;
    if (!head) return;

    head.visible = active;

    if (!active) return;

    const progress = t / 3;
    const x = startX + dir.x * progress * 22;
    const y = startY + dir.y * progress * 22;
    const z = startZ + dir.z * progress * 22;
    head.position.set(x, y, z);

    const fade = 1 - Math.pow(progress, 1.5);
    if (tailMatRef.current) tailMatRef.current.opacity = fade * 0.75;
    if (headMatRef.current) headMatRef.current.opacity = fade * 0.95;
  });

  return (
    <group ref={headRef}>
      {/* Голова метеора */}
      <mesh renderOrder={50}>
        <sphereGeometry args={[0.022, 8, 8]} />
        <meshBasicMaterial
          ref={headMatRef}
          color="#FFF8E0"
          transparent
          opacity={0.95}
          depthWrite={false}
        />
      </mesh>
      {/* Хвост */}
      <mesh renderOrder={49}>
        <tubeGeometry args={[tailCurve, 12, 0.008, 4]} />
        <meshBasicMaterial
          ref={tailMatRef}
          color="#FFE8A0"
          transparent
          opacity={0.7}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

function computeRankBands(bands, totalWidth, gap) {
  if (bands <= 0) return [];

  const gapsTotal = gap * Math.max(0, bands - 1);
  const width = (totalWidth - gapsTotal) / bands;

  const res = [];
  let cursor = 0;

  for (let i = 0; i < bands; i++) {
    const inner = cursor;
    const outer = cursor + width;

    res.push({ inner, outer });

    cursor = outer + gap;
  }

  return res;
}

function bandColor(visualIndex, totalBands) {
  const inner = new THREE.Color('#7E0097');
  const outer = new THREE.Color('#D9A6E6');

  if (!totalBands || totalBands <= 1) return `#${inner.getHexString()}`;

  const t = visualIndex / (totalBands - 1);
  const tt = Math.pow(t, 1.05);

  return inner.clone().lerp(outer, tt);
}

function rankLineWidth(rankIndex, totalRanks) {
  const max = 0.018; // внутри потолще
  const min = 0.004; // снаружи потоньше

  if (!totalRanks || totalRanks <= 1) return max;

  const t = rankIndex / (totalRanks - 1);
  const tt = Math.pow(t, 1.15);
  return max + (min - max) * tt;
}

function RankDivider3D({ radius, y, color = '#7E0097', width = 0.012, onClick, isHighlighted }) {
  const hitWidth = Math.max(width, 0.09);
  const lineWidth = isHighlighted ? width * 1.2 : width;
  const lineOpacity = isHighlighted ? 0.42 : 0.28;
  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, y + 0.082, 0]}>
      <mesh renderOrder={2500}>
        <ringGeometry args={[Math.max(0, radius - lineWidth / 2), radius + lineWidth / 2, 64]} />
        <meshBasicMaterial
          color={isHighlighted ? '#E9C6FE' : color}
          transparent
          opacity={lineOpacity}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>

      <mesh onClick={onClick}>
        <ringGeometry args={[Math.max(0, radius - hitWidth / 2), radius + hitWidth / 2, 64]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function GalaxyOuterHalo({ y, radius, color = '#BD23DB', opacity = 0.12 }) {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, y + 0.002, 0]}
      renderOrder={2330}
    >
      <ringGeometry args={[radius, radius + 0.64, 64]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        depthTest={false}
      />
    </mesh>
  );
}

function getRankRadiusOnBorder(depInner, ringWidth) {
  const OUT_PAD = 0.02;
  return depInner + ringWidth + OUT_PAD;
}

function normalizeLeagues(leaguesRaw) {
  const arr = Array.isArray(leaguesRaw) ? leaguesRaw : [];

  const EXCLUDED = new Set(['вне лиг']);

  const getKey = (l) => {
    const c = (l?.code ?? l?.name ?? '').toString().trim();
    const n = Number(c);
    if (c !== '' && Number.isFinite(n)) return { kind: 0, n };
    return { kind: 1, s: c.toUpperCase() };
  };

  return arr
    .filter(l => {
      const name = String(l?.name ?? '').trim().toLowerCase();
      const code = String(l?.code ?? '').trim().toLowerCase();
      return !EXCLUDED.has(name) && !EXCLUDED.has(code);
    })
    .slice()
    .sort((a, b) => {
      const A = getKey(a);
      const B = getKey(b);
      if (A.kind !== B.kind) return A.kind - B.kind;
      if (A.kind === 0) return A.n - B.n;
      return A.s.localeCompare(B.s, 'ru');
    });
}

function getLeagueIndexOuterFirstByCode(leagueCode, leaguesOrdered) {
  if (!leagueCode) return null;
  const code = String(leagueCode).trim().toUpperCase();
  const idx = (leaguesOrdered || []).findIndex(
    (l) => String(l?.code ?? '').trim().toUpperCase() === code
  );
  return idx >= 0 ? idx : null;
}

const SALES_CODES = new Set(['ГОС', 'ГСК', 'КОМ']);
const SALES_VIRTUAL_ID = -999;

const ZKV_CODES = new Set(['ЗКП', 'ВЭД']);
const ZKV_VIRTUAL_ID = -998;

/** Текст с эффектом печатающей машинки. */
function TypewriterText({ text, active, charsPerSecond = 45, className }) {
  const [visibleLength, setVisibleLength] = useState(0);
  const full = String(text ?? '');
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setVisibleLength(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    setVisibleLength(0);
    const delay = 1000 / charsPerSecond;
    const step = Math.max(1, Math.ceil(full.length / 80));
    intervalRef.current = setInterval(() => {
      setVisibleLength((n) => {
        const next = Math.min(n + step, full.length);
        if (next >= full.length && intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        return next;
      });
    }, delay * step);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, full, charsPerSecond]);

  const visible = full.slice(0, visibleLength);
  const parts = visible.split(/\n/g);

  return (
    <span className={className}>
      {parts.map((p, idx) => (
        <React.Fragment key={idx}>
          {p}
          {idx < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </span>
  );
}

function separateDotsTopView(dots, iterations = 80, minDistMultiplier = 2.8) {
  if (!dots.length) return dots;

  const pts = dots.map(d => ({
    ...d,
    x: d.r * Math.cos(d.angle),
    z: d.r * Math.sin(d.angle),
  }));

  for (let iter = 0; iter < iterations; iter++) {
    // расталкивание
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[j].x - pts[i].x;
        const dz = pts[j].z - pts[i].z;
        const dist = Math.sqrt(dx * dx + dz * dz) || 1e-9;
        const md = Math.max(
          (pts[i].avatarRadius ?? 0.035) * minDistMultiplier,
          (pts[j].avatarRadius ?? 0.035) * minDistMultiplier,
        );
        if (dist < md) {
          const push = (md - dist) / 2;
          const nx = dx / dist;
          const nz = dz / dist;
          pts[i].x -= nx * push;
          pts[i].z -= nz * push;
          pts[j].x += nx * push;
          pts[j].z += nz * push;
        }
      }
    }

    // clamp обратно в кольцо после каждой итерации
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const inner = p.bandInner;
      const outer = p.bandOuter;
      if (inner == null || outer == null) continue;

      const curR = Math.sqrt(p.x * p.x + p.z * p.z) || 1e-9;
      const margin = (p.avatarRadius ?? 0.035) * 1.0;
      const minR = inner + margin;
      const maxR = outer - margin;

      if (maxR <= minR) continue; // кольцо слишком узкое — не трогаем

      if (curR < minR || curR > maxR) {
        const clampedR = Math.max(minR, Math.min(maxR, curR));
        const scale = clampedR / curR;
        p.x *= scale;
        p.z *= scale;
      }
    }
  }

  return pts.map(p => ({
    ...p,
    r: Math.sqrt(p.x * p.x + p.z * p.z),
    angle: Math.atan2(p.z, p.x),
  }));
}

function ConnectionLinesUpdater({ highlightEmpId, activeDots, activeMyDot, avatarPositions, onUpdate }) {
  useFrame(() => {
    if (highlightEmpId == null) { onUpdate([]); return; }

    const centerDot =
      activeDots.find((d) => Number(d.empId) === Number(highlightEmpId)) ??
      (activeMyDot && Number(activeMyDot.empId) === Number(highlightEmpId) ? activeMyDot : null);

    if (!centerDot) { onUpdate([]); return; }

    const start = avatarPositions.current.get(Number(highlightEmpId)) ?? dotToPosition(centerDot);

    const segs = activeDots
      .filter((d) => Number(d.empId) !== Number(highlightEmpId))
      .map((d) => {
        const end = avatarPositions.current.get(Number(d.empId)) ?? dotToPosition(d);
        return { start: [...start], end: [...end] };
      });

    onUpdate(segs);
  });
  return null;
}

export default function Structure() {
  const navigate = useNavigate();
  const isMobile = useMemo(() =>
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []
  );
  const [deps, setDeps] = useState([]);
  const [depsRaw, setDepsRaw] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [leagues, setLeagues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allEmployees, setAllEmployees] = useState([]);
  const [connectionLineSegments, setConnectionLineSegments] = useState([]);
  const [isTopView, setIsTopView] = useState(true);
  const [isViewTransition, setIsViewTransition] = useState(false);
  //  выбранный отдел и данные для карточки
  const [activeDepId, setActiveDepId] = useState(null);
  const [showDepInfo, setShowDepInfo] = useState(false); // оверлей с информацией об отделе (печатающая машинка + блюр)
  const [highlightEmpId, setHighlightEmpId] = useState(null);
  // Если у сотрудника есть зоны ответственности — вместо "связей" показываем только отделы из этих зон
  const [highlightRespEmpId, setHighlightRespEmpId] = useState(null);
  const [highlightRespDepIds, setHighlightRespDepIds] = useState(null); // Set<number> | null
  const [structureLevelInfo, setStructureLevelInfo] = useState(null); // { kind: 'league' | 'rank', name, description, depId?, levelIndex? }
  const [topViewDepOverride, setTopViewDepOverride] = useState(null); // мобилка: top-view только по отделу; на ПК то же даёт topViewSelectedDepId из activeDepId
  const leaguesOrdered = useMemo(() => normalizeLeagues(leagues), [leagues]);
  const avatarWorldPositions = useRef(new Map());
  const lastAvatarTapRef = useRef({ time: 0, x: null, y: null });
  const lastConnectionsShownAtRef = useRef(0);
  const registerAvatarTap = (ev) => {
    const x = ev?.clientX;
    const y = ev?.clientY;
    lastAvatarTapRef.current = {
      time: Date.now(),
      x: typeof x === 'number' ? x : null,
      y: typeof y === 'number' ? y : null,
    };
  };
  const shouldIgnoreLevelClickBecauseRecentAvatarTap = (ev) => {
    const last = lastAvatarTapRef.current;
    const dt = Date.now() - (last?.time ?? 0);
    if (!last?.time || dt > 360) return false;

    const cx = ev?.clientX;
    const cy = ev?.clientY;

    if (
      typeof cx === 'number' &&
      typeof cy === 'number' &&
      typeof last.x === 'number' &&
      typeof last.y === 'number'
    ) {
      return Math.hypot(cx - last.x, cy - last.y) < 75;
    }

    return dt < 250;
  };
  const leaguesCount = leaguesOrdered.length;
  const myDepId = useMemo(() => {
    const raw = me?.department_id != null ? Number(me.department_id) : null;
    if (raw == null) return null;

    // если это один из отделов продаж (ГОС/ГСК/КОМ) — считаем, что это виртуальный отдел "ПРО"
    const dep = depsRaw.find(d => Number(d?.id) === raw);

    //  если это “Руководство”, в структуре “моего отдела” нет
    const depNameLower = String(dep?.name || '').trim().toLowerCase();
    if (depNameLower === 'руководство') return null;

    const code = String(dep?.code || '').trim().toUpperCase();

    if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
    if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
    return raw;
  }, [me, depsRaw]);

  const responsibilityDepIdsByEmpId = useMemo(() => {
    const isLeadership = (d) => String(d?.name || '').trim().toLowerCase() === 'руководство';

    const toEffectiveDepIdByRawId = (rawDepId) => {
      if (rawDepId == null) return null;
      const depObj = depsRaw.find(d => Number(d?.id) === Number(rawDepId));
      if (!depObj) return Number(rawDepId);
      if (isLeadership(depObj)) return null;
      const code = String(depObj?.code || '').trim().toUpperCase();
      if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
      if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
      return depObj?.id != null ? Number(depObj.id) : Number(rawDepId);
    };

    const out = new Map();
    const pool = [
      ...(Array.isArray(allEmployees) ? allEmployees : []),
      ...(me ? [me] : []),
    ];

    for (const emp of pool) {
      const empId = emp?.id != null ? Number(emp.id) : null;
      if (empId == null) continue;

      const rawResp = Array.isArray(emp?.responsibility_departments)
        ? emp.responsibility_departments
        : [];

      const ids = rawResp
        .map((rd) => {
          const rawId = rd != null && typeof rd === 'object' ? rd.id : rd;
          return toEffectiveDepIdByRawId(rawId);
        })
        .filter((v) => v != null)
        .map(Number);

      const unique = new Set();
      const deduped = [];
      for (const id of ids) {
        const n = Number(id);
        if (Number.isNaN(n)) continue;
        if (unique.has(n)) continue;
        unique.add(n);
        deduped.push(n);
      }

      out.set(empId, deduped);
    }

    return out;
  }, [allEmployees, me, depsRaw]);

  const handleEmployeeClick = useCallback((empIdRaw) => {
    const empId = empIdRaw != null ? Number(empIdRaw) : null;
    if (empId == null || Number.isNaN(empId)) return;

    const respDepIds = responsibilityDepIdsByEmpId.get(empId) || [];
    const hasResp = respDepIds.length > 0;

    if (hasResp) {
      const isLeadership = (d) => String(d?.name || '').trim().toLowerCase() === 'руководство';
      const toEffectiveDepIdByRawId = (rawDepId) => {
        if (rawDepId == null) return null;
        const depObj = depsRaw.find(d => Number(d?.id) === Number(rawDepId));
        if (!depObj) return Number(rawDepId);
        if (isLeadership(depObj)) return null;
        const code = String(depObj?.code || '').trim().toUpperCase();
        if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
        if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
        return depObj?.id != null ? Number(depObj.id) : Number(rawDepId);
      };

      const emp =
        allEmployees.find(e => Number(e?.id) === empId) ??
        (me && Number(me?.id) === empId ? me : null);

      const primaryDepId = toEffectiveDepIdByRawId(emp?.department_id);
      const depSet = new Set(respDepIds.map(Number));
      if (primaryDepId != null) depSet.add(Number(primaryDepId));

      setHighlightEmpId(null);
      const isSame = Number(highlightRespEmpId) === empId;
      setHighlightRespEmpId((prev) => {
        const next = Number(prev) === empId ? null : empId;
        setHighlightRespDepIds(next == null ? null : depSet);
        return next;
      });
      if (!isSame) {
        lastConnectionsShownAtRef.current = Date.now();
      }
      return;
    }

    // обычный режим — связи
    setHighlightRespEmpId(null);
    setHighlightRespDepIds(null);
    const isSame = Number(highlightEmpId) === empId;
    setHighlightEmpId((prev) => (Number(prev) === empId ? null : empId));
    if (!isSame) {
      lastConnectionsShownAtRef.current = Date.now();
    }
  }, [responsibilityDepIdsByEmpId, depsRaw, allEmployees, me, highlightRespEmpId, highlightEmpId]);

  const getEmployeeConnectionIds = useCallback((empIdRaw) => {
    const empId = empIdRaw != null ? Number(empIdRaw) : null;
    if (empId == null || Number.isNaN(empId)) return new Set();

    const emp =
      allEmployees.find((e) => Number(e?.id) === empId) ??
      (me && Number(me?.id) === empId ? me : null);

    const ids = new Set(
      (Array.isArray(emp?.connections) ? emp.connections : []).map(Number)
    );
    ids.add(empId);

    const managerId = emp?.manager_id != null ? Number(emp.manager_id) : null;
    if (managerId != null && !Number.isNaN(managerId)) ids.add(managerId);

    return ids;
  }, [allEmployees, me]);

  useEffect(() => {
    let alive = true;

    async function fetchJson(endpoint) {
      const res = await apiFetch(endpoint);
      if (!res.ok) {
        let msg = `${endpoint} -> ${res.status}`;
        try {
          const j = await res.json();
          msg = j?.detail || msg;
        } catch { }
        throw new Error(msg);
      }
      return res.json();
    }

    (async () => {
      try {
        setIsLoading(true);
        const [depsMerged, depsRawData, ranksData, leaguesData, meData, allEmployeesData] = await Promise.all([
          fetchJson(`${API_URL}/structure/departments?merge_sales=1&merge_zkv=1`),
          fetchJson(`${API_URL}/structure/departments`),
          fetchJson(`${API_URL}/structure/ranks`),
          fetchJson(`${API_URL}/structure/leagues`),
          fetchJson(`${API_URL}/employees/me/account`),
          fetchJson(`${API_URL}/employees/search/?query=`)
        ]);

        if (!alive) return;
        const myId = meData?.id ?? meData?.employee_id ?? null;
        const meFromSearch = myId != null
          ? (Array.isArray(allEmployeesData) ? allEmployeesData : (allEmployeesData?.items ?? []))
            .find(e => Number(e?.id) === Number(myId))
          : null;

        const mergedMe = meFromSearch
          ? { ...meFromSearch, ...meData }
          : meData;
        const mergedList = Array.isArray(depsMerged) ? depsMerged : [];
        const rawList = Array.isArray(depsRawData) ? depsRawData : [];

        const isLeadership = (d) =>
          String(d?.name || '').trim().toLowerCase() === 'руководство';

        setDeps(mergedList.filter(d => !isLeadership(d)));

        setDepsRaw(rawList);
        setRanks(Array.isArray(ranksData) ? ranksData : []);
        setLeagues(Array.isArray(leaguesData) ? leaguesData : []);
        setMe(mergedMe || null);
        setAllEmployees(Array.isArray(allEmployeesData) ? allEmployeesData : (allEmployeesData?.items ?? []));
      } catch (e) {
        if (!alive) return;
        setErr(String(e?.message || e));
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const focusedDepId = activeDepId;
  //  подгружаем превью сотрудников для выбранного отдела
  useEffect(() => {
    let alive = true;

    async function fetchJson(endpoint) {
      const res = await apiFetch(endpoint);
      if (!res.ok) {
        let msg = `${endpoint} -> ${res.status}`;
        try {
          const j = await res.json();
          msg = j?.detail || msg;
        } catch { }
        throw new Error(msg);
      }
      return res.json();
    }

    (async () => {
      if (activeDepId == null) {
        return;
      }

      try {
        await fetchJson(
          `${API_URL}/structure/departments/${Number(activeDepId)}/employees`
        );
        if (!alive) return;
      } catch (e) {
        if (!alive) return;
      } finally {
        if (!alive) return;
      }
    })();

    return () => {
      alive = false;
    };
  }, [activeDepId]);

  const layerGap = 0.5;

  const baseInner = 0.0;
  const ringWidth = 1.45;

  const sortedRanks = useMemo(() => {
    return ranks
      .filter((r) => (r?.name || '').toString().trim().toLowerCase() !== 'вне рангов')
      .slice()
      .sort(sortByCode);
  }, [ranks]);

  const orderedDeps = useMemo(() => {
    const isLeadership = (d) =>
      String(d?.name || '').trim().toLowerCase() === 'руководство';

    return [...deps]
      .filter(d => !isLeadership(d))
      .sort((a, b) => {
        const ao = (a?.sort_order ?? 1e9);
        const bo = (b?.sort_order ?? 1e9);
        if (ao !== bo) return ao - bo;
        return String(a?.name || '').localeCompare(String(b?.name || ''), 'ru');
      });
  }, [deps]);

  const activeDep = useMemo(() => {
    if (activeDepId == null) return null;
    return (
      orderedDeps.find((d) => Number(d?.id) === Number(activeDepId)) || null
    );
  }, [orderedDeps, activeDepId]);

  const maxOuterR = useMemo(() => baseInner + ringWidth, [baseInner, ringWidth]);

  //  максимальная высота стека отделов (верхний слой)
  const yMax = useMemo(() => {
    if (!orderedDeps.length) return 0;
    return (orderedDeps.length - 1) * layerGap;
  }, [orderedDeps.length, layerGap]);

  const controlsRef = useRef(null);
  const avatarInteractionLockRef = useRef(false);
  const orbitLastMoveAtRef = useRef(0);
  const VIEW_SNAPSHOT_KEY = 'structure_view_snapshot_v1';
  const pendingRestoreRef = useRef(null);
  const restoreAppliedRef = useRef(false);
  const pointerStateRef = useRef({
    depId: null,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const CLICK_MOVE_TOLERANCE = 8;
  const targetY = yMax * 0.5;
  const activeDepFocusY = useMemo(() => {
    if (activeDepId == null) return null;
    const idx = orderedDeps.findIndex((od) => Number(od?.id) === Number(activeDepId));
    if (idx < 0) return null;
    const yBase = (orderedDeps.length - 1 - idx) * layerGap;
    return yBase;
  }, [activeDepId, orderedDeps, layerGap]);

  const mobileTiltDeg = useMemo(() => {
    if (!isMobile) return 50;
    const hasConnections = highlightEmpId != null;
    const hasResponsibilities = highlightRespDepIds != null && highlightRespDepIds.size > 0;
    // Для связей и зон ответственности диск ставим ближе к «нормальному» боковому виду.
    if (hasConnections || hasResponsibilities) return 10;
    return 30;
  }, [isMobile, highlightEmpId, highlightRespDepIds]);

  useEffect(() => {
    const c = controlsRef.current;
    if (!c) return;
    c.target.set(0, targetY, 0);
    c.update();
  }, [targetY]);

  // При клике по человеку в боковом режиме чуть отдаляем камеру,
  // если она слишком близко, чтобы связи и отдел были видны целиком.
  const ensureSideViewSpaceForPerson = useCallback(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const cam = controls.object;
    const target = controls.target ?? new THREE.Vector3(0, targetY, 0);

    const dir = new THREE.Vector3().subVectors(cam.position, target);
    const dist = dir.length() || 1e-6;

    const MIN_DIST = isMobile ? 0.5 : 2.2;
    if (dist >= MIN_DIST) return;

    dir.setLength(MIN_DIST);
    cam.position.copy(target).add(dir);
    controls.update();
  }, [targetY, isMobile]);

  const saveStructureViewSnapshot = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const c = controlsRef.current;
      const cameraSnap = c
        ? {
          pos: c.object?.position?.toArray?.() ?? null,
          target: c.target?.toArray?.() ?? null,
        }
        : null;

      const highlightRespDepIdsArr = highlightRespDepIds instanceof Set
        ? Array.from(highlightRespDepIds)
        : null;

      const snap = {
        ui: {
          isTopView,
          isViewTransition,
          activeDepId,
          topViewDepOverride,
          showDepInfo,
          highlightEmpId,
          highlightRespEmpId,
          highlightRespDepIds: highlightRespDepIdsArr,
          structureLevelInfo,
        },
        camera: cameraSnap,
      };

      window.sessionStorage.setItem(VIEW_SNAPSHOT_KEY, JSON.stringify(snap));
    } catch {
      // sessionStorage может быть недоступен (например, приватный режим)
    }
  }, [
    VIEW_SNAPSHOT_KEY,
    isTopView,
    isViewTransition,
    activeDepId,
    topViewDepOverride,
    showDepInfo,
    highlightEmpId,
    highlightRespEmpId,
    highlightRespDepIds,
    structureLevelInfo,
  ]);

  const applyStructureViewSnapshot = useCallback((snap) => {
    if (!snap) return;
    const c = controlsRef.current;
    if (!c) return;

    const pos = snap?.pos;
    const target = snap?.target;
    if (Array.isArray(pos) && pos.length === 3) {
      c.object.position.set(pos[0], pos[1], pos[2]);
    }
    if (Array.isArray(target) && target.length === 3) {
      c.target.set(target[0], target[1], target[2]);
    }
    c.update();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(VIEW_SNAPSHOT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      pendingRestoreRef.current = parsed?.camera ?? null;
      const ui = parsed?.ui ?? {};

      if (ui?.isTopView != null) setIsTopView(!!ui.isTopView);
      if (ui?.isViewTransition != null) setIsViewTransition(!!ui.isViewTransition);
      if (ui?.activeDepId !== undefined) setActiveDepId(ui.activeDepId);
      if (ui?.topViewDepOverride !== undefined) setTopViewDepOverride(ui.topViewDepOverride);
      if (ui?.showDepInfo !== undefined) setShowDepInfo(!!ui.showDepInfo);
      if (ui?.highlightEmpId !== undefined) setHighlightEmpId(ui.highlightEmpId);
      if (ui?.highlightRespEmpId !== undefined) setHighlightRespEmpId(ui.highlightRespEmpId);
      if (ui?.highlightRespDepIds) {
        setHighlightRespDepIds(new Set(ui.highlightRespDepIds));
      } else if (ui?.highlightRespDepIds === null) {
        setHighlightRespDepIds(null);
      }
      if (ui?.structureLevelInfo !== undefined) setStructureLevelInfo(ui.structureLevelInfo ?? null);

      window.sessionStorage.removeItem(VIEW_SNAPSHOT_KEY);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (restoreAppliedRef.current) return;
    if (isLoading) return;
    const snapCamera = pendingRestoreRef.current;
    if (!snapCamera) return;

    let cancelled = false;
    let tries = 0;
    const maxTries = 20;

    const tryApply = () => {
      if (cancelled) return;
      tries += 1;
      const c = controlsRef.current;
      if (c) {
        applyStructureViewSnapshot(snapCamera);
        restoreAppliedRef.current = true;
        pendingRestoreRef.current = null;
        return;
      }
      if (tries < maxTries) setTimeout(tryApply, 50);
    };

    tryApply();
    return () => {
      cancelled = true;
    };
  }, [isLoading, applyStructureViewSnapshot]);

  const goToAccountWithSnapshot = useCallback((empId) => {
    saveStructureViewSnapshot();
    navigate(`/account/${empId}`);
  }, [navigate, saveStructureViewSnapshot]);

  const leagueBands = useMemo(() => {
    const bands = leaguesCount;
    if (bands <= 0) return [];

    const weights = Array.from({ length: bands }, (_, visualIndex) =>
      visualIndex === 0 ? 2 : 1
    );
    const totalWeight = weights.reduce((s, w) => s + w, 0);

    const gap = 0;
    const gapsTotal = gap * Math.max(0, bands - 1);
    const unitWidth = (ringWidth - gapsTotal) / totalWeight;

    const res = [];
    let cursor = 0;

    for (let i = 0; i < bands; i++) {
      const width = unitWidth * weights[i];
      res.push({ inner: cursor, outer: cursor + width });
      cursor += width + gap;
    }

    return res;
  }, [leaguesCount, ringWidth]);

  const rankBands = useMemo(() => {
    const bands = sortedRanks.length;
    if (bands <= 0) return [];

    const avgW = ringWidth / bands;
    const gap = Math.min(0.06, avgW * 0.8);

    return computeRankBands(bands, ringWidth, gap);
  }, [sortedRanks.length, ringWidth]);

  const rankDividerBands = useMemo(() => {
    const count = Math.min(sortedRanks.length, leagueBands.length);
    return leagueBands.slice(-count).reverse();
  }, [sortedRanks.length, leagueBands]);

  const rankDividerRadii = useMemo(() => {
    return rankDividerBands.map((b) => baseInner + b.outer);
  }, [rankDividerBands, baseInner]);

  // ---- NEW: партнёрские отделы (в порядке визуальной сортировки, как диски) ----
  const partnerDepIdsOrdered = useMemo(() => {
    const empId = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;
    if (empId == null) return [];

    const isLeadership = (d) =>
      String(d?.name || '').trim().toLowerCase() === 'руководство';

    const toEffectiveDepId = (depObj) => {
      const code = String(depObj?.code || '').trim().toUpperCase();
      if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
      if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
      return depObj?.id != null ? Number(depObj.id) : null;
    };

    const effectiveSet = new Set();

    for (const d of depsRaw) {
      if (isLeadership(d)) continue;

      const partners = Array.isArray(d?.partners) ? d.partners : [];
      if (!partners.map(Number).includes(Number(empId))) continue;

      const eff = toEffectiveDepId(d);
      if (eff == null) continue;

      // не добавляем основной отдел в список partner‑отделов,
      // чтобы не дублировать собственную точку на своём диске
      if (myDepId != null && Number(eff) === Number(myDepId)) continue;

      effectiveSet.add(Number(eff));
    }

    return orderedDeps
      .map(d => Number(d?.id))
      .filter(id => effectiveSet.has(id));
  }, [me, depsRaw, myDepId, orderedDeps]);

  const primaryPartnerDepId = partnerDepIdsOrdered[0] ?? null;

  const hasOwnDepartment = useMemo(() => {
    if (myDepId == null) return false;
    return orderedDeps.some(d => Number(d?.id) === Number(myDepId));
  }, [myDepId, orderedDeps]);

  // В top-view с выбранным отделом: на мобилке задаётся topViewDepOverride, на ПК — activeDepId при наклоне камеры.
  const topViewSelectedDepId = useMemo(() => {
    if (topViewDepOverride != null) return Number(topViewDepOverride);
    if (!isMobile && isTopView && activeDepId != null) return Number(activeDepId);
    return null;
  }, [topViewDepOverride, isMobile, isTopView, activeDepId]);

  const effectiveMyDepIdForTop = useMemo(() => {
    if (topViewSelectedDepId != null) return Number(topViewSelectedDepId);

    if (hasOwnDepartment) return myDepId;

    if (primaryPartnerDepId != null) return Number(primaryPartnerDepId);

    return myDepId; // на всякий
  }, [topViewSelectedDepId, hasOwnDepartment, myDepId, primaryPartnerDepId]);

  // ---- NEW: маркеры партнёра на внешнем кольце всех partner-отделов ----
  const partnerDots = useMemo(() => {
    if (!me || !orderedDeps.length) return [];
    if (!partnerDepIdsOrdered.length) return [];

    const myRankNameLower = (me.rank?.name || '').toString().trim().toLowerCase();
    const hasValidRankName = !!me.rank && myRankNameLower !== 'вне рангов';

    const myLeagueCode = pickLeagueCode(me);
    const lgIdx = getLeagueIndexOuterFirstByCode(myLeagueCode, leaguesOrdered);
    const bands = leagueBands.length;
    const visualIndex = (lgIdx != null && bands) ? (bands - 1) - lgIdx : null;
    const lgBand = (visualIndex != null) ? leagueBands[visualIndex] : null;

    const myBandIndex = lgBand
      ? leagueBands.indexOf(lgBand)
      : leagueBands.length - 1;

    const myT = leagueBands.length > 1
      ? myBandIndex / (leagueBands.length - 1)
      : 1;

    const avatarRadius = 0.025 + Math.pow(myT, 1.5) * 0.040;
    const avatarRadiusForClamp = isTopView ? avatarRadius : 0.055 * 1.35;

    // --- если есть лига: рисуем аватар на кольце лиги
    let leagueR = null;
    if (lgBand != null) {
      const desired = (lgBand.inner + lgBand.outer) / 2;
      leagueR = clampRadiusInBand(lgBand.inner, lgBand.outer, desired, avatarRadiusForClamp);
    }

    // --- если нет лиги, но есть ранг: рисуем между кольцами
    let rankR = null;
    if (leagueR == null && hasValidRankName) {
      rankR = getRankRadiusOnBorder(baseInner, ringWidth);
    }

    // если нет ни лиги, ни ранга — нечего рисовать
    if (leagueR == null && rankR == null) return [];

    const empId = me.id ?? me.employee_id ?? me.employee?.id ?? null;
    const teamId = me.team_id != null ? Number(me.team_id) : 0;
    const avatarUrl = me.avatar_thumb || me.avatar_url || null;

    return partnerDepIdsOrdered
      .map((depId) => {
        const depIndex = orderedDeps.findIndex(d => Number(d?.id) === Number(depId));
        const layerIndex = depIndex >= 0 ? depIndex : 0;

        const yBase = (orderedDeps.length - 1 - layerIndex) * layerGap + 0.08;

        const y =
          isTopView && effectiveMyDepIdForTop != null && Number(depId) === Number(effectiveMyDepIdForTop)
            ? yMax + 0.08
            : yBase;

        const angle = ((Number(depId) * 1.33 + teamId * 0.9) % (Math.PI * 2));
        if (rankR == null && leagueR == null) return [];

        const bandInner = leagueR != null
          ? (lgBand ? lgBand.inner : null)
          : (rankR != null ? rankR - 0.02 : null);
        const bandOuter = leagueR != null
          ? (lgBand ? lgBand.outer : null)
          : (rankR != null ? rankR + 0.02 : null);

        if (leagueR != null) {
          return {
            depId,
            y,
            r: leagueR,
            angle,
            type: 'league',
            avatarUrl,
            empId,
            avatarRadius,
            avatarRadiusSide: 0.055,
            bandInner,
            bandOuter,
          };
        }

        if (rankR != null) {
          return {
            depId,
            y,
            r: rankR,
            angle,
            type: 'rank',
            avatarUrl,
            empId,
            avatarRadius,
            avatarRadiusSide: 0.055,
            bandInner,
            bandOuter,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [
    me,
    orderedDeps,
    partnerDepIdsOrdered,
    leagueBands,
    layerGap,
    isTopView,
    effectiveMyDepIdForTop,
    yMax,
    leaguesOrdered,
  ]);

  // ---- LEGACY: старый одиночный маркер (оставляем как есть, но не используем если есть partnerDots) ----
  const dot = useMemo(() => {
    if (!me || !orderedDeps.length) return null;

    //  Если у пользователя "вне рангов" — вообще не показываем маркер
    const myRankNameLower = (me.rank?.name || '').toString().trim().toLowerCase();
    const hasValidRankName = !!me.rank && myRankNameLower !== 'вне рангов';

    const empId = me.id ?? me.employee_id ?? me.employee?.id ?? null;

    const isLeadership = (d) =>
      String(d?.name || '').trim().toLowerCase() === 'руководство';

    const toEffectiveDepId = (depObj) => {
      const code = String(depObj?.code || '').trim().toUpperCase();
      if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
      if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
      return depObj?.id != null ? Number(depObj.id) : null;
    };

    let rawDepId = me.department_id != null ? Number(me.department_id) : null;
    const teamId = me.team_id != null ? Number(me.team_id) : null;

    const rawDepObj = rawDepId != null
      ? depsRaw.find(d => Number(d?.id) === rawDepId)
      : null;

    //  если текущий отдел "Руководство" — считаем, что “своего отдела” нет (для структуры)
    let depId = (rawDepId != null && rawDepObj && !isLeadership(rawDepObj))
      ? toEffectiveDepId(rawDepObj)
      : null;

    //  если департамент отсутствует/скрыт (например, Руководство), но сотрудник является partner —
    // ставим на первом (по sort_order = order в orderedDeps) отделе, где он partner
    const depHiddenOrMissing =
      depId == null ||
      orderedDeps.findIndex(d => Number(d?.id) === Number(depId)) < 0;

    if (!hasOwnDepartment && depHiddenOrMissing && empId != null && depsRaw.length) {
      const partnerEffectiveIds = new Set();

      for (const d of depsRaw) {
        // не считаем "Руководство" как целевой диск
        if (isLeadership(d)) continue;

        const partners = Array.isArray(d?.partners) ? d.partners : [];
        if (!partners.map(Number).includes(Number(empId))) continue;

        const eff = toEffectiveDepId(d);
        if (eff != null) partnerEffectiveIds.add(Number(eff));
      }

      // выбираем первый по сортировке = первый в orderedDeps
      if (partnerEffectiveIds.size) {
        const first = orderedDeps.find(x => partnerEffectiveIds.has(Number(x?.id)));
        if (first?.id != null) depId = Number(first.id);
      }
    }

    const myRankId = me.rank?.id != null ? Number(me.rank.id) : null;
    const myRankCode = me.rank?.code ?? null;

    const depIndex = orderedDeps.findIndex((d) => Number(d?.id) === Number(depId));
    const layerIndex = depIndex >= 0 ? depIndex : 0;

    let rankIndex = -1; // логический индекс в sortedRanks (0..7)
    if (myRankId != null) {
      rankIndex = sortedRanks.findIndex((r) => Number(r?.id) === myRankId);
    }
    if (rankIndex < 0 && myRankCode) {
      rankIndex = sortedRanks.findIndex(
        (r) => (r.code || '').toLowerCase() === String(myRankCode).toLowerCase()
      );
    }

    const myLeagueCode = pickLeagueCode(me);

    // Определяем "Вне Лиг" по полным данным лиг из исходного массива leagues,
    // а не по отфильтрованному leaguesOrdered (где "Вне Лиг" уже выкинута).
    const myLeagueFull =
      me?.league_id != null
        ? leagues.find(l => Number(l.id) === Number(me.league_id))
        : null;

    const myLeagueNameLower = (
      me.league?.name ||
      myLeagueFull?.name ||
      ''
    ).toString().trim().toLowerCase();

    const myLeagueCodeLower = (
      me.league?.code ||
      myLeagueFull?.code ||
      ''
    ).toString().trim().toLowerCase();
    const isOutOfLeagues =
      myLeagueNameLower === 'вне лиг' ||
      myLeagueCodeLower === 'вне лиг';

    const lgIdx = getLeagueIndexOuterFirstByCode(myLeagueCode, leaguesOrdered);
    const bands = leagueBands.length;
    const visualIndex = (lgIdx != null && bands) ? (bands - 1) - lgIdx : null;
    const lgBand = (visualIndex != null) ? leagueBands[visualIndex] : null;

    const canUseLeague = lgBand != null && !isOutOfLeagues;

    const canUseRank = !canUseLeague && !isOutOfLeagues && hasValidRankName && rankIndex >= 0;

    if (!canUseLeague && !canUseRank && !isOutOfLeagues) return null;

    let mode = 'orbit';
    if (canUseLeague) mode = 'league';
    else if (canUseRank) mode = 'rank';

    let r = null;
    let rankRadius = null;
    const myBandIndex = lgBand
      ? leagueBands.indexOf(lgBand)
      : leagueBands.length - 1;

    const myT = leagueBands.length > 1
      ? myBandIndex / (leagueBands.length - 1)
      : 1;

    let avatarRadius = 0.025 + Math.pow(myT, 1.5) * 0.040;
    const avatarRadiusForClamp = isTopView ? avatarRadius : 0.055 * 1.35;

    if (mode === 'orbit') {
      r = maxOuterR * 1.18;
    } else if (mode === 'league') {
      const desired = (lgBand.inner + lgBand.outer) / 2;
      r = clampRadiusInBand(lgBand.inner, lgBand.outer, desired, avatarRadiusForClamp);
    } else {
      // --- rank (1 = снаружи, больше = ближе к центру)
      rankRadius = rankDividerRadii[rankIndex];

      if (rankRadius == null) {
        rankRadius = getRankRadiusOnBorder(baseInner, ringWidth);
      }
      r = rankRadius;
    }

    const angle = teamId ? ((teamId * 0.9) % (Math.PI * 2)) : Math.PI * 0.35;
    const yBase = (orderedDeps.length - 1 - layerIndex) * layerGap + 0.08;

    // если мы сверху — поднимаем и маркер на верхний слой
    const y = isTopView && effectiveMyDepIdForTop != null && depId === effectiveMyDepIdForTop ? yMax + 0.08 : yBase;

    const avatarUrl = me.avatar_thumb || me.avatar_url || null;

    const bandInner =
      mode === 'league'
        ? (lgBand ? lgBand.inner : null)
        : mode === 'rank'
          ? (rankRadius != null ? rankRadius - 0.02 : null)
          : null;

    const bandOuter =
      mode === 'league'
        ? (lgBand ? lgBand.outer : null)
        : mode === 'rank'
          ? (rankRadius != null ? rankRadius + 0.02 : null)
          : null;

    if (mode === 'rank') {
      return {
        mode: 'rank',
        depId,
        y,
        r,
        angle,
        avatarUrl,
        empId,
        avatarRadius,
        avatarRadiusSide: 0.055,
        bandInner,
        bandOuter,
      };
    }
    if (mode === 'league') {
      return {
        mode: 'league',
        depId,
        y,
        r,
        angle,
        avatarUrl,
        empId,
        avatarRadius,
        avatarRadiusSide: 0.055,
        bandInner,
        bandOuter,
      };
    }
    return {
      mode: 'orbit',
      depId,
      y,
      r,
      angle,
      avatarUrl,
      empId,
      avatarRadius,
      avatarRadiusSide: 0.055,
      isOrbit: true,
      bandInner,
      bandOuter,
    };
  }, [me, orderedDeps, sortedRanks, leagueBands, rankDividerRadii, leagues, baseInner, layerGap, isTopView, effectiveMyDepIdForTop, yMax, depsRaw, hasOwnDepartment, leaguesOrdered, maxOuterR]);

  const allEmployeeDots = useMemo(() => {
    if (!orderedDeps.length || !leagueBands.length) return [];

    const bands = leaguesOrdered.length;
    if (!bands) return [];

    const myEmpId = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;
    const showMyDotsInAllEmployeeDots =
      highlightRespDepIds != null &&
      highlightRespDepIds.size > 0 &&
      highlightRespEmpId != null &&
      myEmpId != null &&
      Number(highlightRespEmpId) === Number(myEmpId);

    const isLeadership = (d) => String(d?.name || '').trim().toLowerCase() === 'руководство';

    const toEffectiveDepIdByRawId = (rawDepId) => {
      if (rawDepId == null) return null;
      const depObj = depsRaw.find(d => Number(d?.id) === Number(rawDepId));
      if (!depObj) return Number(rawDepId);
      if (isLeadership(depObj)) return null;
      const code = String(depObj?.code || '').trim().toUpperCase();
      if (SALES_CODES.has(code)) return SALES_VIRTUAL_ID;
      if (ZKV_CODES.has(code)) return ZKV_VIRTUAL_ID;
      return depObj?.id != null ? Number(depObj.id) : Number(rawDepId);
    };

    const getEmployeeDepIds = (emp) => {
      const primaryDepId = toEffectiveDepIdByRawId(emp?.department_id);
      const rawResp = Array.isArray(emp?.responsibility_departments)
        ? emp.responsibility_departments
        : [];

      const respDepIds = rawResp
        .map((rd) => {
          const rawId = rd != null && typeof rd === 'object' ? rd.id : rd;
          return toEffectiveDepIdByRawId(rawId);
        })
        .filter((v) => v != null)
        .map(Number);

      const unique = new Set();
      const out = [];

      if (primaryDepId != null) {
        const n = Number(primaryDepId);
        unique.add(n);
        out.push(n);
      }

      for (const depId of respDepIds) {
        const n = Number(depId);
        if (Number.isNaN(n)) continue;
        if (unique.has(n)) continue;
        unique.add(n);
        out.push(n);
      }

      return { primaryDepId: primaryDepId != null ? Number(primaryDepId) : null, depIds: out };
    };

    const getLeagueBandByCode = (leagueCodeRaw) => {
      if (!leagueCodeRaw) return null;
      const leagueCode = String(leagueCodeRaw).trim().toUpperCase();
      if (!leagueCode) return null;
      const lgIdx = getLeagueIndexOuterFirstByCode(leagueCode, leaguesOrdered);
      if (lgIdx == null) return null;
      const visualIndex = (bands - 1) - lgIdx;
      return leagueBands[visualIndex] || null;
    };

    const getRankBand = (conn) => {
      const rankId = conn?.rank_id != null ? Number(conn.rank_id) : null;
      const rankCode = conn?.rank_code != null ? String(conn.rank_code).toLowerCase() : null;

      let rankIndex = -1;

      if (rankId != null) {
        rankIndex = sortedRanks.findIndex((r) => Number(r?.id) === rankId);
      }

      if (rankIndex < 0 && rankCode) {
        rankIndex = sortedRanks.findIndex(
          (r) => String(r?.code || '').toLowerCase() === rankCode
        );
      }

      if (rankIndex < 0) return null;

      return rankDividerRadii[rankIndex] ?? null;
    };

    const selectedDepIdNum = !isTopView && activeDepId != null ? Number(activeDepId) : null;
    const selectedDepEmployeeIds = (() => {
      if (selectedDepIdNum == null) return null;

      const ids = [];
      const sel = selectedDepIdNum;
      const myEmpIdNum = myEmpId != null ? Number(myEmpId) : null;

      const myRespIds = myEmpIdNum != null
        ? (responsibilityDepIdsByEmpId.get(myEmpIdNum) ?? [])
        : [];
      const allMyDiscIds = new Set([
        ...(myDepId != null ? [Number(myDepId)] : []),
        ...myRespIds.map(Number),
      ]);
      const allowMyDot = myEmpIdNum != null && (showMyDotsInAllEmployeeDots || allMyDiscIds.size > 1);

      for (const emp of allEmployees) {
        const empIdNum = emp?.id != null ? Number(emp.id) : null;
        if (empIdNum == null) continue;

        if (empIdNum === myEmpIdNum && !allowMyDot) continue;

        const { depIds } = getEmployeeDepIds(emp);
        if (!depIds.includes(sel)) continue;
        ids.push(empIdNum);
      }

      return Array.from(new Set(ids)).sort((a, b) => a - b);
    })();
    const selectedDepTotal = selectedDepEmployeeIds?.length ?? 0;
    const selectedDepIndexByEmpId = selectedDepEmployeeIds
      ? new Map(selectedDepEmployeeIds.map((id, idx) => [Number(id), idx]))
      : null;

    // Для 7‑й лиги равномерно раскладываем людей по окружности
    // внутри каждого отдела, поэтому заранее собираем "корзины".
    const league7Buckets = new Map(); // key: `${depId}|7` → number[]

    const employeesForDots = (() => {
      const byId = new Map();
      for (const emp of allEmployees) {
        const id = emp?.id != null ? Number(emp.id) : null;
        if (id != null) byId.set(id, emp);
      }
      if (me?.id != null) {
        const id = Number(me.id);
        byId.set(id, { ...(byId.get(id) || {}), ...me });
      }
      return Array.from(byId.values());
    })();

    employeesForDots.forEach((emp) => {
      const empId = emp?.id != null ? Number(emp.id) : null;
      if (empId == null) return;

      const { depIds } = getEmployeeDepIds(emp);
      if (!depIds.length) return;

      const leagueFull =
        emp?.league_id != null
          ? leagues.find(l => Number(l.id) === Number(emp.league_id))
          : null;

      const leagueNameLower = (
        emp?.league?.name ||
        leagueFull?.name ||
        ''
      ).toString().trim().toLowerCase();

      const leagueCodeUpper = (
        emp?.league?.code ||
        leagueFull?.code ||
        ''
      ).toString().trim().toUpperCase();

      if (leagueNameLower === 'вне лиг' || leagueCodeUpper === 'ВНЕ ЛИГ') return;
      if (leagueCodeUpper !== '7') return;

      for (const depId of depIds) {
        const key = `${depId}|7`;
        if (!league7Buckets.has(key)) league7Buckets.set(key, []);
        league7Buckets.get(key).push(empId);
      }
    });

    return employeesForDots
      .flatMap((emp) => {
        const empId = emp?.id != null ? Number(emp.id) : null;
        if (empId == null) return [];

        const myRespIds = responsibilityDepIdsByEmpId.get(empId) ?? [];
        const allMyDiscIds = new Set([
          ...(myDepId != null ? [Number(myDepId)] : []),
          ...myRespIds.map(Number),
        ]);
        const hasMultipleZones = allMyDiscIds.size > 1;

        if (empId === myEmpId && !showMyDotsInAllEmployeeDots) {
          // В top-view «я» обычно рисуется через dot/partnerDots, но при нескольких
          // зонах ответственности нужны точки и сверху (иначе видна только первая зона).
          if (isTopView && !hasMultipleZones) return [];
          if (!isTopView && !hasMultipleZones) return [];
        }

        let { primaryDepId, depIds: allDepIds } = getEmployeeDepIds(emp);

        if (
          showMyDotsInAllEmployeeDots &&
          empId === myEmpId &&
          highlightRespDepIds != null &&
          highlightRespDepIds.size
        ) {
          const unique = new Set(allDepIds.map(Number));
          for (const depId of highlightRespDepIds) {
            const n = Number(depId);
            if (Number.isNaN(n) || unique.has(n)) continue;
            unique.add(n);
            allDepIds.push(n);
          }
        }

        if (!allDepIds.length) return [];

        const leagueFull =
          emp?.league_id != null
            ? leagues.find(l => Number(l.id) === Number(emp.league_id))
            : null;

        const leagueNameLower = (
          emp?.league?.name ||
          leagueFull?.name ||
          ''
        ).toString().trim().toLowerCase();

        const leagueCodeLower = (
          emp?.league?.code ||
          leagueFull?.code ||
          ''
        ).toString().trim().toLowerCase();

        const isOutOfLeagues =
          leagueNameLower === 'вне лиг' ||
          leagueCodeLower === 'вне лиг';

        const lgBand = (() => {
          if (isOutOfLeagues) return null;
          const leagueCodeRaw = (emp?.league?.code ?? leagueFull?.code ?? null);
          if (!leagueCodeRaw) return null;
          return getLeagueBandByCode(leagueCodeRaw);
        })();
        const rankRadius = (!lgBand && !isOutOfLeagues) ? getRankBand(emp) : null;

        const avatarRadius = 0.035;
        const baseNoise = ((empId * 127 + 31) % 997) / 997;

        return allDepIds.map((depId, depSlot) => {
          const depIndex = orderedDeps.findIndex(d => Number(d?.id) === Number(depId));
          if (depIndex < 0) return null;

          const yBase = (orderedDeps.length - 1 - depIndex) * layerGap + 0.08;
          const y = isTopView ? (yMax + 0.08) : yBase;

          let r;
          let angle;

          const isInnerRing = rankRadius != null && !lgBand && !isOutOfLeagues;

          if (isOutOfLeagues) {
            r = maxOuterR * 1.18;
            const angleBase = (empId * 2.618 + Number(depId || 0) * 1.114 + depSlot * 0.73) % (Math.PI * 2);
            const angleJitter = (baseNoise - 0.5) * 0.55;
            angle = angleBase + angleJitter;
          } else if (isInnerRing) {
            r = rankRadius;
            angle = (empId * 2.618) % (Math.PI * 2);
          } else {
            if (lgBand) {
              const leagueCode =
                (emp?.league?.code ?? leagueFull?.code ?? null) != null
                  ? String(emp?.league?.code ?? leagueFull?.code).trim().toUpperCase()
                  : '';
              const isLeague7 = leagueCode === '7';

              const bandCenter = (lgBand.inner + lgBand.outer) / 2;
              const halfSpan = (lgBand.outer - lgBand.inner) * 0.42;

              if (isLeague7) {
                const key = `${depId}|7`;
                const bucket = league7Buckets.get(key) || [];
                const total = bucket.length || 1;
                const idxInBucket = bucket.indexOf(empId);
                const safeIdx = idxInBucket >= 0 ? idxInBucket : 0;

                const offset = (Number(depId || 0) * 0.37) % (Math.PI * 2);
                angle = ((safeIdx / total) * Math.PI * 2 + offset) % (Math.PI * 2);

                if (isTopView) {
                  const centered = (total > 1 ? safeIdx / (total - 1) : 0.5) - 0.5;
                  r = bandCenter + centered * halfSpan;
                } else {
                  const margin = avatarRadius * 1.2;
                  const minR = lgBand.inner + margin;
                  const maxR = lgBand.outer - margin;
                  const tR = total > 1 ? (safeIdx % Math.ceil(Math.sqrt(total))) / (Math.ceil(Math.sqrt(total)) - 1 || 1) : 0.5;
                  r = maxR > minR ? minR + tR * (maxR - minR) : bandCenter;
                }
              } else {
                const margin = avatarRadius * 0.6;
                const minR = lgBand.inner + margin;
                const maxR = lgBand.outer - margin;
                const radiusNoise = ((empId * 47) % 313) / 313;
                r = maxR > minR ? minR + radiusNoise * (maxR - minR) : bandCenter;

                const angleBase = (empId * 2.618) % (Math.PI * 2);
                const smallJitter = (baseNoise - 0.5) * 0.25;
                angle = angleBase + smallJitter;
              }
            } else {
              const lastBand = leagueBands[leagueBands.length - 1];
              if (lastBand) {
                // последнее внешнее кольцо без лиги — фиксированный радиус + чуть‑чуть шума
                const bandCenter = (lastBand.inner + lastBand.outer) / 2;
                const span = (lastBand.outer - lastBand.inner) * 0.25;
                const centeredNoise = (baseNoise - 0.5) * span;
                r = bandCenter + centeredNoise;

                const angleBase = (empId * 2.618) % (Math.PI * 2);
                const smallJitter = (baseNoise - 0.5) * 0.25;
                angle = angleBase + smallJitter;
              } else {
                r = maxOuterR * 0.7;
                const angleBase = (empId * 2.618) % (Math.PI * 2);
                const smallJitter = (baseNoise - 0.5) * 0.25;
                angle = angleBase + smallJitter;
              }
            }
          }

          if (selectedDepIndexByEmpId && selectedDepIdNum != null && Number(depId) === Number(selectedDepIdNum)) {
            const leagueCode =
              (emp?.league?.code ?? (emp?.league_id != null ? leagues.find(l => Number(l.id) === Number(emp.league_id))?.code : null) ?? null) != null
                ? String(emp?.league?.code ?? leagues.find(l => Number(l.id) === Number(emp.league_id))?.code).trim().toUpperCase()
                : '';
            const isLeague7 = leagueCode === '7';

            if (!isLeague7) {
              const total = Math.max(1, selectedDepTotal);
              const idx = selectedDepIndexByEmpId.get(Number(empId)) ?? 0;
              const angleOffset = ((Number(selectedDepIdNum) * 0.17) % 1) * (Math.PI * 2);
              angle = angleOffset + (idx / total) * (Math.PI * 2);

              if (isOutOfLeagues) {
                r = maxOuterR * 1.05;
              } else if (lgBand) {
                const bandCenter = (lgBand.inner + lgBand.outer) / 2;
                const inset = avatarRadius * 0.9;
                const minR = lgBand.inner + inset;
                const maxR = lgBand.outer - inset;
                r = THREE.MathUtils.clamp(bandCenter, minR, maxR);
              } else if (isInnerRing && rankRadius != null) {
                r = rankRadius;
              } else {
                const lastBand = leagueBands.length ? leagueBands[leagueBands.length - 1] : null;
                if (lastBand) {
                  r = (lastBand.inner + lastBand.outer) / 2;
                } else {
                  r = maxOuterR * 0.7;
                }
              }
            }
          }

          const avatarUrl = emp?.avatar_thumb ?? emp?.avatar_url ?? emp?.avatar ?? null;
          const isPrimary = depId === primaryDepId;

          return {
            empId,
            depId,
            r,
            angle,
            y,
            avatarUrl,
            avatarRadius,
            avatarRadiusSide: 0.055,
            isOrbit:
              selectedDepIndexByEmpId && selectedDepIdNum != null && Number(depId) === Number(selectedDepIdNum)
                ? false
                : isOutOfLeagues,
            isPrimary,
            bandInner: lgBand ? lgBand.inner : (rankRadius != null ? rankRadius - 0.02 : null),
            bandOuter: lgBand ? lgBand.outer : (rankRadius != null ? rankRadius + 0.02 : null),
          };
        }).filter(Boolean);
      })
      .filter(Boolean);
  }, [
    allEmployees,
    orderedDeps,
    maxOuterR,
    leagues,
    sortedRanks,
    rankDividerRadii,
    depsRaw,
    isTopView,
    leaguesOrdered,
    leagueBands,
    yMax,
    me,
    myDepId,
    responsibilityDepIdsByEmpId,
    activeDepId,
    highlightRespDepIds,
    highlightRespEmpId,
  ]);

  const visibleConnectionDots = useMemo(() => {
    if (isViewTransition) return [];

    const dedupeByEmpPreferPrimary = (dots) => {
      const byEmp = new Map();
      for (const cd of dots) {
        const key = Number(cd.empId);
        const existing = byEmp.get(key);
        if (!existing) {
          byEmp.set(key, cd);
          continue;
        }
        if (cd.isPrimary && !existing.isPrimary) {
          byEmp.set(key, cd);
        }
      }
      return Array.from(byEmp.values());
    };

    if (highlightRespDepIds != null && highlightRespDepIds.size) {
      if (highlightRespEmpId == null) return [];

      let dots = allEmployeeDots.filter(
        (cd) =>
          Number(cd.empId) === Number(highlightRespEmpId) &&
          highlightRespDepIds.has(Number(cd.depId))
      );

      if (isTopView && topViewSelectedDepId != null) {
        dots = dots.filter(
          (cd) => Number(cd.depId) === Number(topViewSelectedDepId)
        );
      }

      return dots;
    }

    if (highlightEmpId != null) {
      const connIds = getEmployeeConnectionIds(highlightEmpId);
      return dedupeByEmpPreferPrimary(
        allEmployeeDots.filter((cd) => connIds.has(Number(cd.empId)))
      );
    }

    if (isTopView) {
      if (topViewSelectedDepId != null) {
        return allEmployeeDots.filter(
          (cd) => Number(cd.depId) === Number(topViewSelectedDepId)
        );
      }
      return dedupeByEmpPreferPrimary(allEmployeeDots);
    }
    if (focusedDepId == null) return [];
    return allEmployeeDots.filter(cd => Number(cd.depId) === Number(focusedDepId));

  }, [isTopView, isViewTransition, me, allEmployeeDots, focusedDepId, highlightEmpId, topViewSelectedDepId, highlightRespDepIds, highlightRespEmpId, getEmployeeConnectionIds]);

  const sideViewSeparatedDots = useMemo(() => {
    if (isTopView) return visibleConnectionDots;
    if (!visibleConnectionDots.length) return visibleConnectionDots;
    return separateDotsTopView(visibleConnectionDots, 120, 3.2);
  }, [isTopView, visibleConnectionDots]);

  const visibleDeps = useMemo(() => {
    if (isTopView) {
      if (highlightRespDepIds != null && highlightRespDepIds.size) {
        return orderedDeps.filter((d) => highlightRespDepIds.has(Number(d.id)));
      }
      if (effectiveMyDepIdForTop == null) return orderedDeps.slice(0, 1);
      return orderedDeps.filter(
        (d) => Number(d.id) === Number(effectiveMyDepIdForTop)
      );
    }

    if (highlightRespDepIds != null && highlightRespDepIds.size) {
      return orderedDeps.filter((d) => highlightRespDepIds.has(Number(d.id)));
    }

    if (highlightEmpId != null && visibleConnectionDots.length) {
      const allowedIds = new Set(
        visibleConnectionDots.map((cd) => Number(cd.depId))
      );
      const myEmpId = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;
      if (dot?.depId != null && myEmpId != null) {
        const connIds = getEmployeeConnectionIds(highlightEmpId);
        if (connIds.has(Number(myEmpId))) allowedIds.add(Number(dot.depId));
      }
      return orderedDeps.filter((d) => allowedIds.has(Number(d.id)));
    }

    if (activeDepId != null) {
      return orderedDeps.filter(
        (d) => Number(d.id) === Number(activeDepId)
      );
    }

    return orderedDeps;
  }, [
    isTopView,
    orderedDeps,
    effectiveMyDepIdForTop,
    highlightEmpId,
    highlightRespDepIds,
    visibleConnectionDots,
    activeDepId,
    dot,
    me,
    getEmployeeConnectionIds,
  ]);

  const visiblePartnerDots = useMemo(() => {
    if (isViewTransition) return [];
    if (highlightRespDepIds != null && highlightRespDepIds.size) return [];
    // Если мы из Руководства (нет своего отдела) — не показываем точки партнёра (это дубли нас),
    // показываем только одну точку (visibleMyDot).
    if (!hasOwnDepartment) return [];

    const myEmpId = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;
    if (highlightEmpId != null && myEmpId != null && Number(highlightEmpId) !== Number(myEmpId)) {
      const connIds = getEmployeeConnectionIds(highlightEmpId);
      if (!connIds.has(Number(myEmpId))) return [];
    }

    if (isTopView) {
      return partnerDots.filter(
        (pd) => effectiveMyDepIdForTop == null
          ? true
          : Number(pd.depId) === Number(effectiveMyDepIdForTop)
      );
    }

    if (focusedDepId == null) return [];

    return partnerDots.filter(
      (pd) => Number(pd.depId) === Number(focusedDepId)
    );
  }, [isTopView, isViewTransition, partnerDots, focusedDepId, effectiveMyDepIdForTop, highlightEmpId, highlightRespDepIds, hasOwnDepartment, me, getEmployeeConnectionIds]);

  const visibleMyDot = useMemo(() => {
    if (isViewTransition) return null;
    if (!dot) return null;
    if (!isTopView && highlightRespDepIds == null && highlightEmpId == null) {
      const myRespIds = dot.empId != null
        ? (responsibilityDepIdsByEmpId.get(Number(dot.empId)) ?? [])
        : [];
      const allMyDiscIds = new Set([
        ...(myDepId != null ? [Number(myDepId)] : []),
        ...myRespIds.map(Number),
      ]);
      if (allMyDiscIds.size > 1) return null;
    }
    if (highlightRespDepIds != null && highlightRespDepIds.size) {
      if (highlightRespEmpId == null) return null;
      const myEmpId = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;
      if (myEmpId != null && Number(highlightRespEmpId) === Number(myEmpId)) return null;
      return Number(dot.empId) === Number(highlightRespEmpId) ? dot : null;
    }
    if (isTopView) {
      const myRespIds = dot.empId != null
        ? (responsibilityDepIdsByEmpId.get(Number(dot.empId)) ?? [])
        : [];
      const allMyDiscIds = new Set([
        ...(myDepId != null ? [Number(myDepId)] : []),
        ...myRespIds.map(Number),
      ]);
      // Несколько зон — маркеры рисуются через allEmployeeDots / visibleConnectionDots
      if (allMyDiscIds.size > 1) return null;

      if (topViewSelectedDepId != null && Number(dot.depId) !== Number(topViewSelectedDepId)) {
        return null;
      }
      return dot;
    }

    const myEmpId = dot.empId;

    if (highlightEmpId != null && Number(highlightEmpId) !== Number(myEmpId)) {
      const connIds = getEmployeeConnectionIds(highlightEmpId);
      if (!connIds.has(Number(myEmpId))) return null;
    }

    if (highlightEmpId != null && Number(highlightEmpId) === Number(myEmpId)) {
      return dot;
    }

    if (focusedDepId == null) return null;
    return Number(dot.depId) === Number(focusedDepId) ? dot : null;
  }, [isTopView, isViewTransition, dot, myDepId, responsibilityDepIdsByEmpId, focusedDepId, highlightEmpId, topViewSelectedDepId, highlightRespDepIds, highlightRespEmpId, getEmployeeConnectionIds, me?.id, me?.employee_id, me?.employee?.id]);

  const topViewSeparatedDots = useMemo(() => {
    if (!isTopView) return { conn: visibleConnectionDots, my: visibleMyDot, partner: visiblePartnerDots };

    const all = [
      ...visibleConnectionDots.map(d => ({ ...d, _src: 'conn' })),
      ...(visibleMyDot ? [{ ...visibleMyDot, _src: 'my' }] : []),
      ...visiblePartnerDots.map(d => ({ ...d, _src: 'partner' })),
    ];

    const separated = separateDotsTopView(all, 30, 3.0);

    return {
      conn: separated.filter(d => d._src === 'conn'),
      my: separated.find(d => d._src === 'my') ?? null,
      partner: separated.filter(d => d._src === 'partner'),
    };
  }, [isTopView, visibleConnectionDots, visibleMyDot, visiblePartnerDots]);

  const myEmpIdForConnMarkers = me?.id ?? me?.employee_id ?? me?.employee?.id ?? null;

  const connDotsForRender = useMemo(() => {
    if (isTopView) {
      return topViewSeparatedDots.my
        ? [...topViewSeparatedDots.conn, topViewSeparatedDots.my]
        : topViewSeparatedDots.conn;
    }
    return visibleMyDot
      ? [...sideViewSeparatedDots, visibleMyDot]
      : sideViewSeparatedDots;
  }, [isTopView, topViewSeparatedDots, sideViewSeparatedDots, visibleMyDot]);

  return (
    <div className={s.page}>
      <div className={s.viewer}>
        <button
          type="button"
          className={s.topClose}
          aria-label="На главную"
          onClick={() => navigate('/plus')}
        >
          ×
        </button>

        {isMobile && (
          <div className={`${s.mobileControls} ${activeDep ? s.mobileControlsTopView : ''}`}>
            {activeDep && (
              <button
                type="button"
                className={s.mobileControlsButton}
                style={{ padding: "0" }}
                onClick={() => {
                  setActiveDepId(Number(activeDepId));
                  setStructureLevelInfo(null);
                  setShowDepInfo(true);
                }}
                aria-label="Информация об отделе"
              >
                <IconPlanet />
              </button>
            )}
            <button
              type="button"
              className={
                isTopView
                  ? `${s.mobileControlsButton} ${s.mobileControlsButtonActive}`
                  : s.mobileControlsButton
              }
              onClick={() => {
                setTopViewDepOverride(activeDep ? Number(activeDepId) : null);
                setIsTopView(true);
                setIsViewTransition(false);
              }}
            >
              Ранги Лиги
            </button>
            <button
              type="button"
              className={
                !isTopView
                  ? `${s.mobileControlsButton} ${s.mobileControlsButtonActive}`
                  : s.mobileControlsButton
              }
              onClick={() => {
                const nextActiveDepId =
                  topViewDepOverride != null ? Number(topViewDepOverride) : null;
                setTopViewDepOverride(null);
                setIsTopView(false);
                setActiveDepId(nextActiveDepId);
                setIsViewTransition(false);
              }}
            >
              {isTopView && activeDep ? activeDep.name : 'Структура компании'}
            </button>
          </div>
        )}

        {err ? (
          <div className={s.loading}>Ошибка: {err}</div>
        ) : isLoading ? (
          <CosmicLoader text="Строим космическую структуру…" />
        ) : (
          <Suspense fallback={<CosmicLoader text="Разворачиваем галактику отделов…" />}>
            <Canvas
              shadows={!isMobile}
              dpr={isMobile ? [1, 1] : [1, 2]}
              gl={{ antialias: !isMobile }}
              camera={{ position: [0, 6, 0.001], fov: 45, near: isMobile ? 0.01 : 0.1 }}
              onPointerMissed={() => {
                const hasRespHighlight =
                  highlightRespEmpId != null ||
                  (highlightRespDepIds != null && highlightRespDepIds.size > 0);
                if (highlightEmpId != null || hasRespHighlight) {
                  if (isMobile) {
                    const now = Date.now();
                    // Если только что показали связи (меньше чем 450 мс назад),
                    // игнорируем первый "промах" по фону, чтобы связи не мигали.
                    const dt = now - (lastConnectionsShownAtRef.current || 0);
                    if (dt < 450) {
                      return;
                    }
                  }
                  setShowDepInfo(false);
                  setHighlightEmpId(null);
                  setHighlightRespEmpId(null);
                  setHighlightRespDepIds(null);
                  return;
                }
                setActiveDepId(null);
                setShowDepInfo(false);
                setHighlightEmpId(null);
                setHighlightRespEmpId(null);
                setHighlightRespDepIds(null);
              }}
              onContextMenu={(e) => e.preventDefault()}
            >
              {isMobile ? (
                <MobileCameraController
                  isTopView={isTopView}
                  radius={maxOuterR}
                  yMax={yMax}
                  focusY={activeDepFocusY}
                  tiltDeg={mobileTiltDeg}
                  controlsRef={controlsRef}
                />
              ) : (
                <>
                  <TopViewTracker
                    onModeChange={setIsTopView}
                    onTransitionChange={setIsViewTransition}
                  />
                  <AutoFitCamera radius={maxOuterR} yMax={yMax} padding={0.55} />
                </>
              )}
              <TextureEvictManager
                allDots={isTopView ? topViewSeparatedDots.conn : sideViewSeparatedDots}
              />

              <ConnectionLinesUpdater
                highlightEmpId={highlightEmpId}
                activeDots={isTopView ? topViewSeparatedDots.conn : sideViewSeparatedDots}
                activeMyDot={isTopView ? topViewSeparatedDots.my : visibleMyDot}
                avatarPositions={avatarWorldPositions}
                onUpdate={setConnectionLineSegments}
              />

              <ambientLight intensity={0.75} />

              <directionalLight
                position={[3, 6, 2]}
                intensity={0.25}
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
                shadow-camera-near={0.1}
                shadow-camera-far={30}
                shadow-camera-left={-6}
                shadow-camera-right={6}
                shadow-camera-top={6}
                shadow-camera-bottom={-6}
              />

              <directionalLight position={[-4, 3, -2]} intensity={0.18} />

              <Starfield quality={isMobile ? 'low' : 'high'} radius={16} />
              <Meteors count={isMobile ? 3 : 6} />
              {connectionLineSegments.map((seg, idx) => (
                <ConnectionLine3D
                  key={`connline_${idx}`}
                  start={seg.start}
                  end={seg.end}
                  phaseOffset={idx * 0.4}
                />
              ))}
              {visibleDeps.map((d) => {
                const depInner = baseInner;
                const layerIndex = Math.max(
                  0,
                  orderedDeps.findIndex((od) => Number(od.id) === Number(d.id))
                );
                const yBase = (orderedDeps.length - 1 - layerIndex) * layerGap;
                const isDepFocused =
                  Number(focusedDepId) === Number(d.id) ||
                  (highlightRespDepIds != null && highlightRespDepIds.has(Number(d.id)));
                const isTopViewRespDep =
                  isTopView &&
                  highlightRespDepIds != null &&
                  highlightRespDepIds.has(Number(d.id));
                // Отдел всегда остаётся на своей "вертикальной" позиции
                const y = isTopView && (
                  isTopViewRespDep ||
                  (effectiveMyDepIdForTop != null && Number(d.id) === Number(effectiveMyDepIdForTop))
                )
                  ? yMax
                  : yBase;

                const onDepPointerDown = (ev) => {
                  ev.stopPropagation();
                  if (isTopView) return;

                  pointerStateRef.current = {
                    depId: Number(d.id),
                    startX: ev.clientX ?? 0,
                    startY: ev.clientY ?? 0,
                    moved: false,
                  };
                };

                const onDepPointerMove = (ev) => {
                  const ps = pointerStateRef.current;
                  if (ps.depId == null) return;

                  const x = ev.clientX ?? 0;
                  const y = ev.clientY ?? 0;

                  const dx = x - ps.startX;
                  const dy = y - ps.startY;

                  if (Math.hypot(dx, dy) > CLICK_MOVE_TOLERANCE) {
                    ps.moved = true;
                  }
                };

                const onDepPointerUp = (ev) => {
                  ev.stopPropagation();
                  if (isTopView) {
                    // в top-view отделы не кликаются вовсе
                    pointerStateRef.current = {
                      depId: null,
                      startX: 0,
                      startY: 0,
                      moved: false,
                    };
                    return;
                  }

                  const ps = pointerStateRef.current;
                  const depIdNum = Number(d.id);

                  const isSameDep = Number(ps.depId) === depIdNum;
                  const isClick = isSameDep && !ps.moved;

                  if (isClick) {
                    setHighlightEmpId(null);
                    setHighlightRespEmpId(null);
                    setHighlightRespDepIds(null);
                    setActiveDepId((prev) => (Number(prev) === depIdNum ? null : depIdNum));
                  }

                  pointerStateRef.current = {
                    depId: null,
                    startX: 0,
                    startY: 0,
                    moved: false,
                  };
                };

                const onDepPointerLeave = () => {
                  // тут ничего не сбрасываем, иначе drag может внезапно сломаться
                };

                const onDepPointerCancel = () => {
                  pointerStateRef.current = {
                    depId: null,
                    startX: 0,
                    startY: 0,
                    moved: false,
                  };
                };


                if (!leaguesOrdered.length) {
                  const geom = makeExtrudedRingGeometry(
                    depInner,
                    depInner + ringWidth,
                    BAND_HEIGHT,
                    96
                  );
                  return (
                    <group
                      key={d.id}
                      onPointerDown={onDepPointerDown}
                      onPointerMove={onDepPointerMove}
                      onPointerUp={onDepPointerUp}
                      onPointerOut={onDepPointerLeave}
                      onPointerCancel={onDepPointerCancel}
                    >
                      <mesh
                        rotation={[-Math.PI / 2, 0, 0]}
                        position={[0, y, 0]}
                        geometry={geom}
                        castShadow
                        receiveShadow
                      >
                        <meshPhysicalMaterial
                          color={PURPLE}
                          transparent={false}
                          opacity={1}
                          roughness={0.25}
                          metalness={0.0}
                          clearcoat={0.65}
                          clearcoatRoughness={0.2}
                          depthWrite={true}
                        />
                        <Edges color={EDGE_HEX} />
                      </mesh>

                      <OrbitGlowRing
                        radius={maxOuterR}
                        y={y}
                        color={isDepFocused ? '#E9C6FE' : '#BD23DB'}
                        width={isDepFocused ? 0.05 : 0.025}
                        opacity={isDepFocused ? 0.3 : 0.16}
                      />

                      {/* Название отдела на центре боковой стенки внешнего кольца */}
                      <DepartmentSideName3D
                        name={d.name}
                        y={y}
                        outerR={maxOuterR}
                        hidden={isTopView}
                        dimmed={highlightEmpId != null || (highlightRespDepIds != null && highlightRespDepIds.size)}
                        showInfoIcon={!isTopView && isDepFocused}
                        onInfoClick={() => {
                          setActiveDepId(Number(d.id));
                          setStructureLevelInfo(null);
                          setShowDepInfo(true);
                        }}
                      />
                    </group>
                  );
                }

                const bands = leaguesOrdered.length;

                return (
                  <group
                    key={d.id}
                    onPointerDown={onDepPointerDown}
                    onPointerMove={onDepPointerMove}
                    onPointerUp={onDepPointerUp}
                    onPointerOut={onDepPointerLeave}
                    onPointerCancel={onDepPointerCancel}
                  >
                    {leaguesOrdered.map((lg, lgIdx) => {
                      const visualIndex = (bands - 1) - lgIdx;

                      const b = leagueBands[visualIndex];
                      if (!b) return null;

                      const innerR = depInner + b.inner;
                      const outerR = depInner + b.outer;

                      if (outerR <= innerR) return null;

                      return (
                        <RankBand3D
                          key={`${d.id}_lg_${lg.id ?? lg.code ?? lgIdx}`}
                          innerR={innerR}
                          outerR={outerR}
                          color={bandColor(visualIndex, bands)}
                          y={y + 0.081}
                          opacity={1}
                          isHighlighted={
                            structureLevelInfo?.kind === 'league' &&
                            Number(structureLevelInfo?.depId) === Number(d.id) &&
                            structureLevelInfo?.levelIndex === lgIdx
                          }
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (isTopView && shouldIgnoreLevelClickBecauseRecentAvatarTap(ev)) return;
                            if (isTopView && topViewSelectedDepId != null) return;
                            if (!isTopView) return;
                            const name = String(lg?.name ?? lg?.code ?? '').trim() || 'Лига';
                            const description =
                              String(lg?.description ?? lg?.about ?? '').trim() || '';
                            setStructureLevelInfo({
                              kind: 'league',
                              name,
                              description,
                              depId: d.id,
                              levelIndex: lgIdx,
                            });
                            setShowDepInfo(false);
                          }}
                        />
                      );
                    })}

                    <GalaxyOuterHalo
                      y={y + 0.079}
                      radius={maxOuterR}
                      color={isDepFocused ? '#E9C6FE' : '#BD23DB'}
                      opacity={isDepFocused ? 0.08 : 0.04}
                    />

                    {rankDividerBands.map((lb, idx) => {
                      const radius = baseInner + lb.outer;
                      const visualIdx = rankBands.length - 1 - idx;
                      const rankIndex =
                        sortedRanks.length > 0
                          ? Math.max(
                            0,
                            Math.min(
                              sortedRanks.length - 1,
                              sortedRanks.length - 1 - visualIdx,
                            ),
                          )
                          : -1;
                      const rankObj = rankIndex >= 0 ? sortedRanks[rankIndex] : null;
                      return (
                        <RankDivider3D
                          key={`${d.id}_rank_div_${idx}`}
                          radius={radius}
                          y={y}
                          color="#7E0097"
                          width={rankLineWidth(visualIdx, rankBands.length)}
                          isHighlighted={
                            structureLevelInfo?.kind === 'rank' &&
                            Number(structureLevelInfo?.depId) === Number(d.id) &&
                            structureLevelInfo?.levelIndex === idx
                          }
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (isTopView && shouldIgnoreLevelClickBecauseRecentAvatarTap(ev)) return;
                            if (isTopView && topViewSelectedDepId != null) return;
                            if (!isTopView) return;
                            if (!rankObj) return;
                            const name = String(rankObj?.name ?? rankObj?.code ?? '').trim() || 'Ранг';
                            const description =
                              String(rankObj?.description ?? rankObj?.about ?? '').trim() || '';
                            setStructureLevelInfo({
                              kind: 'rank',
                              name,
                              description,
                              depId: d.id,
                              levelIndex: idx,
                            });
                            setShowDepInfo(false);
                          }}
                        />
                      );
                    })}

                    <DepartmentSideName3D
                      name={d.name}
                      y={y}
                      outerR={maxOuterR}
                      hidden={isTopView}
                      dimmed={highlightEmpId != null || (highlightRespDepIds != null && highlightRespDepIds.size)}
                      showInfoIcon={!isMobile && !isTopView && isDepFocused}
                      onInfoClick={() => {
                        setActiveDepId(Number(d.id));
                        setStructureLevelInfo(null);
                        setShowDepInfo(true);
                      }}
                    />
                  </group>
                );
              })}

              {connDotsForRender.map((cd) => {
                const isMeDot =
                  myEmpIdForConnMarkers != null &&
                  cd?.empId != null &&
                  Number(cd.empId) === Number(myEmpIdForConnMarkers);

                const radiusTop = isMeDot
                  ? (isTopView && topViewSelectedDepId != null ? cd.avatarRadius * 2.5 : cd.avatarRadius * 1.5)
                  : (isTopView && topViewSelectedDepId != null ? cd.avatarRadius * 2 : cd.avatarRadius);

                const isTopViewWithDeptFocus = isMobile && isTopView && topViewSelectedDepId != null;

                return isTopView && !isTopViewWithDeptFocus ? (
                  <LazyAvatarMarker3D
                    key={`conn_${cd.empId}_${cd.depId}_${isMeDot ? 'me' : 'other'}`}
                    r={cd.r}
                    angle={cd.angle}
                    y={cd.y}
                    avatarUrl={cd.avatarUrl}
                    isOrbit={cd.isOrbit ?? false}
                    radius={radiusTop}
                    onPointerDownSignal={registerAvatarTap}
                    interactionLockRef={avatarInteractionLockRef}
                    orbitLastMoveAtRef={orbitLastMoveAtRef}
                    isHighlighted={
                      (highlightEmpId != null && cd.empId === highlightEmpId) ||
                      (highlightRespEmpId != null && cd.empId === highlightRespEmpId)
                    }
                    isMe={isMeDot ? true : undefined}
                    onClick={() => { /* обычный ПК‑top‑view: клики по людям отключены */ }}
                    onLongPress={() => {
                      if (cd.empId != null) goToAccountWithSnapshot(cd.empId);
                    }}
                    onPositionUpdate={(pos) => avatarWorldPositions.current.set(cd.empId, pos)}
                  />
                ) : (
                  <EagerAvatarMarker3D
                    key={`conn_${cd.empId}_${cd.depId}_${isMeDot ? 'me' : 'other'}`}
                    r={cd.r}
                    angle={cd.angle}
                    y={cd.y}
                    avatarUrl={cd.avatarUrl}
                    isOrbit={cd.isOrbit ?? false}
                    radius={(cd.avatarRadiusSide ?? 0.055) * 1.35}
                    onPointerDownSignal={registerAvatarTap}
                    interactionLockRef={avatarInteractionLockRef}
                    orbitLastMoveAtRef={orbitLastMoveAtRef}
                    isHighlighted={
                      (highlightEmpId != null && cd.empId === highlightEmpId) ||
                      (highlightRespEmpId != null && cd.empId === highlightRespEmpId)
                    }
                    isMe={isMeDot ? true : undefined}
                    onClick={() => {
                      if (!isTopView) {
                        ensureSideViewSpaceForPerson();
                      } else if (isTopViewWithDeptFocus) {
                        setIsTopView(false);
                      }
                      handleEmployeeClick(cd.empId);
                      if (!isTopView && isMeDot && cd?.depId != null) {
                        setActiveDepId(Number(cd.depId));
                      }
                    }}
                    onLongPress={() => {
                      if (cd.empId != null) goToAccountWithSnapshot(cd.empId);
                    }}
                    onPositionUpdate={(pos) => avatarWorldPositions.current.set(cd.empId, pos)}
                  />
                );
              })}

              {(isTopView ? topViewSeparatedDots.partner : visiblePartnerDots).map((pd) => (
                isTopView ?
                  <LazyAvatarMarker3D
                    key={`me_partner_${pd.depId}_${pd.type}`}
                    r={pd.r}
                    angle={pd.angle}
                    y={pd.y}
                    avatarUrl={pd.avatarUrl}
                    radius={pd.avatarRadius * 2}
                    onPointerDownSignal={registerAvatarTap}
                    interactionLockRef={avatarInteractionLockRef}
                    orbitLastMoveAtRef={orbitLastMoveAtRef}
                    onClick={() => {
                      const myEmpId = pd.empId;
                      if (isTopView) return;
                      handleEmployeeClick(myEmpId);
                      if (!isTopView && myDepId != null) {
                        setActiveDepId(Number(myDepId));
                      }
                    }}
                    onLongPress={() => {
                      if (pd.empId != null) goToAccountWithSnapshot(pd.empId);
                    }}
                    isMe={true}
                    onPositionUpdate={(pos) => avatarWorldPositions.current.set(pd.empId, pos)}
                  />
                  :
                  <EagerAvatarMarker3D
                    r={pd.r}
                    angle={pd.angle}
                    y={pd.y}
                    avatarUrl={pd.avatarUrl}
                    radius={
                      (pd.avatarRadiusSide ?? 0.055) * 1.35
                    }
                    onPointerDownSignal={registerAvatarTap}
                    interactionLockRef={avatarInteractionLockRef}
                    orbitLastMoveAtRef={orbitLastMoveAtRef}
                    onClick={() => {
                      const myEmpId = pd.empId;
                      if (isTopView) return;
                      ensureSideViewSpaceForPerson();
                      handleEmployeeClick(myEmpId);
                      if (!isTopView && myDepId != null) {
                        setActiveDepId(Number(myDepId));
                      }
                    }}
                    onLongPress={() => {
                      if (pd.empId != null) goToAccountWithSnapshot(pd.empId);
                    }}
                    onPositionUpdate={(pos) => avatarWorldPositions.current.set(pd.empId, pos)}
                  />
              ))}


              <MobileOrbitSoftener
                controlsRef={controlsRef}
                isMobile={isMobile}
                isTopView={isTopView}
              />

              <OrbitControls
                ref={controlsRef}
                enableDamping
                dampingFactor={
                  isMobile && isTopView ? 0.16
                    : isTopView ? 0.12
                      : 0.08
                }
                onChange={() => {
                  orbitLastMoveAtRef.current = Date.now();
                }}
                onStart={() => {
                  avatarInteractionLockRef.current = true;
                }}
                onEnd={() => {
                  avatarInteractionLockRef.current = false;
                }}
                enablePan={true}
                enableRotate={!isMobile}
                panSpeed={isTopView ? 0.15 : 0.5}
                zoomSpeed={isMobile && isTopView ? 0.5 : 1.0}
                screenSpacePanning={true}
                minDistance={isMobile && !isTopView ? 1.25 : 2.5}
                maxDistance={50}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 2}
                minAzimuthAngle={0}
                maxAzimuthAngle={0}
                touches={{
                  ONE: THREE.TOUCH.PAN,
                  TWO: THREE.TOUCH.DOLLY_PAN
                }}
              />
            </Canvas>
          </Suspense>
        )}

        {/*  Оверлей информации об отделе: блюр фона, текст с печатающей машинкой, клик мимо — закрыть */}
        {showDepInfo && activeDep && (
          <div
            className={s.depInfoOverlay}
            onClick={() => setShowDepInfo(false)}
            role="dialog"
            aria-label="Информация об отделе"
          >
            <div className={s.depInfoScrollable}>
              <TypewriterText
                text={String(activeDep.description ?? '').trim() || '—'}
                active={showDepInfo}
                charsPerSecond={42}
                className={s.depInfoTypewriter}
              />
            </div>
          </div>
        )}

        {structureLevelInfo && (
          <div
            className={s.depInfoOverlay}
            onClick={() => setStructureLevelInfo(null)}
            role="dialog"
            aria-label={
              structureLevelInfo.kind === 'league'
                ? 'Информация о лиге'
                : 'Информация о ранге'
            }
          >
            <div className={s.depInfoScrollable}>
              <TypewriterText
                text={
                  (String(structureLevelInfo.description ?? '').trim() ||
                    String(structureLevelInfo.name ?? '').trim() ||
                    '—')
                }
                active={!!structureLevelInfo}
                charsPerSecond={42}
                className={s.depInfoTypewriter}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  );
}